import type { Messages } from './ar.ts';

/** English UI strings. Same keys as `ar.ts` (checked by the type checker and a test). */
export const en: Messages = {
  app: {
    name: 'Dob Burger',
    product: 'Enterprise Management System',
    skipToContent: 'Skip to content',
  },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    continue: 'Continue',
    back: 'Back',
    loading: 'Loading…',
    retry: 'Try again',
    search: 'Search',
    details: 'Details',
    actions: 'Actions',
    status: 'Status',
    name: 'Name',
    nameAr: 'Name in Arabic',
    nameEn: 'Name in English',
    phone: 'Phone number',
    email: 'Email',
    optional: 'optional',
    required: 'required',
    reason: 'Reason',
    reasonHint: 'Write the reason clearly (at least 5 characters). It is kept in the audit log.',
    none: 'None',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    copy: 'Copy',
    copied: 'Copied',
    noResults: 'No results.',
    comingSoon: 'Soon',
    notProvided: 'Not provided yet',
    companyWide: 'Company-wide',
    unexpectedError: 'Something went wrong. Please try again.',
    networkError: 'Cannot reach the server. Check the internet and try again.',
    errorLabel: 'Error',
    warningLabel: 'Warning',
    successLabel: 'Done',
    infoLabel: 'Note',
    requestId: 'Request id: {{id}}',
    stub: 'Stub (not connected)',
    formNotSaved: 'Not saved: one or more fields have an error',
    formNotSent: 'Not sent: one or more fields have an error',
    iqd: 'IQD',
    formFieldsList: 'Fields: {{fields}}',
  },
  theme: {
    label: 'Appearance',
    system: 'Same as device',
    light: 'Light',
    dark: 'Dark',
  },
  language: {
    label: 'Language',
    switchTo: 'العربية',
    ar: 'العربية',
    en: 'English',
  },
  desktop: {
    build: 'Dob Desktop {{version}}',
    serverStaging: 'Test server',
    serverProduction: 'Production server',
    serverDevelopment: 'Development server on this PC',
    unsigned: 'Unsigned test build',
  },
  connection: {
    checking: 'Checking the connection…',
    online: 'Online',
    offline: 'No internet',
    serverDown: 'Server not responding',
    onlineOnly:
      'The admin web needs the internet. Daily branch operations work offline in Dob Desktop.',
    desktopNotRegistered:
      'This PC is not registered in the system yet, so it works only with the internet and keeps no offline operations.',
    pending: 'Not sent yet: {{count}}',
    rejected: 'Refused, need review: {{count}}',
    allSent: 'Everything sent',
  },
  auth: {
    signInTitle: 'Sign in',
    signInSubtitle: 'Dob Burger administration',
    identifier: 'Phone number or email',
    identifierHint: 'Example: 07701234567',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    forgotPassword: 'Forgot your password?',
    signOut: 'Sign out',
    sessionEnded: 'Your session has ended. Please sign in again.',
    idleSignedOut:
      'You were signed out automatically because this PC was not used for a while, to protect your account. Please sign in again.',
    mfaTitle: 'Two-step verification',
    mfaSubtitle:
      'Open your authenticator app (e.g. Google Authenticator) and enter the 6-digit code.',
    code: 'Code',
    verify: 'Verify',
    useRecoveryCode: 'I do not have my phone — use a recovery code',
    useAuthenticator: 'Use the authenticator app',
    recoveryCode: 'Recovery code',
    recoveryCodeHint: 'One of the ten codes you saved when you set this up. Each code works once.',
    setupTitle: 'Set up two-step verification',
    setupIntro:
      'Yours is a management account, so it must be protected by an authenticator app on your phone.',
    setupStep1: 'Install Google Authenticator or Microsoft Authenticator on your phone.',
    setupStep2:
      'In the app tap (+), then “Scan a QR code”, and point the camera at the code below.',
    setupStep3: 'Enter the 6-digit code the app shows.',
    setupManual: 'Cannot scan? Enter this key manually:',
    qrAlt: 'QR code to add the account to the authenticator app',
    recoveryTitle: 'Recovery codes',
    recoveryIntro:
      'Keep these ten codes somewhere safe (on paper, locked away). If you lose your phone, each code lets you sign in once.',
    recoveryWarning: 'These codes will not be shown again.',
    recoverySaved: 'I have saved the codes somewhere safe',
    recoveryDownload: 'Download as a text file',
    recoveryFileTitle: 'Recovery codes — Dob Burger',
    forgotTitle: 'Reset your password',
    forgotIntro: 'Enter your phone number. We will send a 6-digit code by WhatsApp (or email).',
    channel: 'Send by',
    channelWhatsapp: 'WhatsApp',
    channelEmail: 'Email',
    sendCode: 'Send the code',
    codeSent: 'If the account exists, the code has been sent. It is valid for 10 minutes.',
    newPassword: 'New password',
    repeatPassword: 'Repeat the password',
    passwordsDiffer: 'The two passwords do not match.',
    passwordRules:
      'At least 8 characters (16 for management accounts that can sign in without internet); do not use your phone number or easy words like 12345678.',
    passwordRulesLong:
      'At least 16 characters; do not use your phone number or easy words. Several words with spaces are easy to remember.',
    longPasswordTitle: 'Your password must be at least 16 characters',
    longPasswordBody:
      'Yours is a management account that can sign in without internet on branch PCs, so a protected copy of the password is kept on the main PC of the branch. A long password keeps that copy safe if the PC is ever stolen.',
    mustChangeLongTitle: 'Choose a longer password',
    mustChangeLongIntro:
      'Your account needs a password of at least 16 characters before you continue.',
    setPassword: 'Save the password',
    passwordReset: 'Your password has been changed. Sign in with it now.',
    backToSignIn: 'Back to sign in',
    mustChangeTitle: 'Change your temporary password',
    mustChangeIntro: 'You signed in with a temporary password. Choose your own password.',
    currentPassword: 'Current password',
    currentPasswordWrong: 'The current password is incorrect.',
    changePassword: 'Change password',
    passwordChanged: 'Your password has been changed. Your other devices were signed out.',
  },
  nav: {
    groupPurchases: 'Purchasing',
    suppliers: 'Suppliers',
    purchaseInvoices: 'Purchase invoices',
    receiveGoods: 'Receive goods',
    receiptExcess: 'Receipt differences',
    menu: 'Menu',
    groupOverview: 'Overview',
    groupPeople: 'People and access',
    groupOrganization: 'Organization',
    groupControl: 'Control',
    groupFinance: 'Finance',
    groupWarehouse: 'Warehouse',
    dashboard: 'Home',
    inbox: 'Notifications',
    users: 'Users',
    roles: 'Roles and permissions',
    locations: 'Branches and locations',
    devices: 'Devices',
    settings: 'Settings',
    audit: 'Audit log',
    emergency: 'Emergency Mode',
    approvals: 'Approvals',
    syncIssues: 'Sync issues',
    messages: 'Message log',
    health: 'System health and backups',
    ledger: 'Ledger',
    items: 'Items',
    itemCategories: 'Item categories',
    waste: 'Waste',
    openings: 'Opening stock',
    stockMatch: 'Stock and the books',
    stockHere: 'Stock by location',
    account: 'My account',
  },
  location: {
    switcher: 'Current location',
    switcherHint: 'Permissions and data follow the selected location',
    allLocations: 'All locations',
    typeBRANCH: 'Branch',
    typeWAREHOUSE: 'Warehouse',
    typeHEAD_OFFICE: 'Head office',
    ownershipOWNED: 'Company-owned',
    ownershipFRANCHISE: 'Franchise',
    ownershipPARTNERSHIP: 'Partnership',
    royalty: 'Royalty: {{rate}} of sales',
    active: 'Active',
    inactive: 'Not active yet',
    code: 'Code',
    city: 'City',
    address: 'Address',
    phones: 'Phone',
    type: 'Type',
    ownership: 'Ownership',
    // M8-5 (O39): what the website and the customer app show.
    publicInfo: 'Public information',
    publicInfoTitle: 'Public information — {{name}}',
    publicInfoIntro:
      'This is shown on the website and in the customer app. A branch appears only while it is active and marked "shown to the public". An empty field is never shown.',
    nameAr: 'Name in Arabic',
    nameEn: 'Name in English',
    addressAr: 'Address in Arabic',
    addressEn: 'Address in English',
    addressHint: 'Leave it empty until the address is confirmed; nothing is shown in its place.',
    publicPhones: 'Public phone numbers',
    publicPhonesHint: 'One number per line (up to 3), e.g. 0772 835 0100.',
    hoursGiven: 'Opening hours are known',
    hoursHint: 'Baghdad time. A close before the open means after midnight (e.g. 12:00 to 02:00).',
    day: 'Day',
    opens: 'Opens',
    closes: 'Closes',
    closedDay: 'Closed',
    // Owner, 2026-10-04: open 24 hours, the same hours for every day, times in the 24-hour form.
    dayMode: 'Status',
    openTimes: 'Opens and closes',
    open24: 'Open 24 hours',
    timeHint: '24-hour form, e.g. 09:30 or 23:00.',
    badTime: 'Type the time in the 24-hour form, e.g. 09:30 or 14:00.',
    sameForAll: 'Same hours for all days',
    sameForAllHint: "Copies Saturday's hours to the other days.",
    warnTitle: 'Check: unusual hours',
    warnIntro: 'Please check these days before saving:',
    warnShort: '{{day}}: open less than an hour ({{open}} – {{close}}).',
    warnNight: '{{day}}: opens after midnight and before 5 in the morning ({{open}}).',
    warnAlmostAllDay: '{{day}}: open 20 hours or more. Did you mean "Open 24 hours"?',
    saveAnyway: 'Save anyway',
    backToEdit: 'Back to editing',
    isPublic: 'Shown to the public (website and customer app)',
    isPublicHint: 'Branches only. An inactive branch is not shown even when marked.',
    isPublicNotBranch: 'The warehouse and the head office are never shown to the public.',
    shownPublicly: 'Shown to the public',
    notShownPublicly: 'Not shown to the public',
    saved: 'Public information saved.',
  },
  /** Days of the week, Saturday first (the Iraqi week). */
  week: {
    sat: 'Saturday',
    sun: 'Sunday',
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
  },
  /** The public website (M8-5): home and branches. */
  site: {
    brand: 'Dob Burger',
    metaDescription:
      'Dob Burger — our branches, phone numbers and opening hours. Ordering from the site and the app is coming soon.',
    home: 'Home',
    branches: 'Branches',
    otherLanguage: 'العربية',
    heroTitle: 'Welcome to Dob Burger',
    comingSoon: 'Coming soon: order from the website and the Dob Burger app.',
    appsSoon: 'The Dob Burger app is coming soon to Google Play and the App Store.',
    seeBranches: 'Our branches and phone numbers',
    branchesTitle: 'Our branches',
    branchesIntro: 'Call the branch directly.',
    noBranches: 'No branches are shown right now.',
    unavailable: 'The branches cannot be shown right now. Please try again shortly.',
    call: 'Call',
    hours: 'Opening hours',
    closed: 'Closed',
    allDay: 'Open 24 hours',
    am: 'AM',
    pm: 'PM',
    staging: 'Test version of the website — not for public use.',
    rights: '© Dob Burger',
  },
  /** The customer app "Dob Burger" (M8-5); the rest of its words are the site's. */
  customer: {
    comingSoon: 'Coming soon: order in the Dob Burger app.',
    loading: 'Loading the branches…',
    savedList: 'No internet — this is the last saved list.',
    retry: 'Try again',
  },
  dashboard: {
    welcome: 'Welcome, {{name}}',
    intro: 'This is the Dob Burger administration. What you see depends on your permissions.',
    systemStatus: 'System status',
    database: 'Database',
    queue: 'Message queue',
    storage: 'File storage',
    backups: 'Backups',
    ok: 'Working',
    down: 'Down',
    version: 'Version: {{commit}}',
    myRoles: 'My roles',
    unread: 'Unread notifications',
    pendingApprovals: 'Approvals waiting for a decision',
    openSyncIssues: 'Open sync issues',
    open: 'Open',
    whatsNext: 'Coming screens',
    whatsNextBody:
      'This is the first version of the admin web. Screens marked “Soon” are built in the next parts, after your feedback.',
  },
  users: {
    phoneHint:
      'Iraqi mobile: 11 digits starting with 07. Foreign number: type + and the country code.',
    title: 'Users',
    subtitle: 'Everyone who uses the system: partners, management and branch staff.',
    searchPlaceholder: 'Search by name or phone number',
    newUser: 'New user',
    create: 'Create user',
    created: 'User created',
    tempPasswordTitle: 'Temporary password',
    tempPasswordIntro:
      'Give this password to the employee in person. It is shown only once, and they must change it at their first sign-in.',
    preferredLang: 'Employee language',
    statusACTIVE: 'Active',
    statusSUSPENDED: 'Suspended',
    statusLEFT: 'Left',
    twoFactor: 'Two-step verification',
    twoFactorOn: 'On',
    twoFactorOff: 'Off',
    pin: 'Cashier PIN',
    pinSet: 'Set',
    pinNotSet: 'Not set',
    roles: 'Roles',
    noRoles: 'No current roles.',
    roleScope: 'Scope',
    since: 'Since',
    until: 'Until',
    ended: 'Ended',
    assignRole: 'Add role',
    role: 'Role',
    roleNeeds2fa: 'This is a management role and requires two-step verification.',
    scopeLocationRequired: 'This role is given for one location. Choose the location.',
    endRole: 'End role',
    endRoleTitle: 'End role “{{role}}”',
    suspend: 'Suspend account',
    suspendTitle: 'Suspend {{name}}',
    suspendIntro:
      'They are signed out of every device at once and cannot sign in until reactivated.',
    reactivate: 'Reactivate',
    reactivateTitle: 'Reactivate {{name}}',
    resetTitle: 'Reset',
    resetPassword: 'Reset password',
    resetTwoFactor: 'Reset two-step verification',
    resetPin: 'Clear PIN',
    resetIntro:
      'This is recorded in the audit log. Resetting a management account alerts all partners.',
    resetDone: 'Reset done.',
    notAllowedHint:
      'You cannot act on an account with more permissions than yours, or on your own account.',
    back: 'All users',
    editDetails: 'Edit details',
    editTitle: 'Edit details of {{name}}',
    editIntro:
      'The change is kept in the audit log (old value, new value and reason). Changing the phone or email of a management account alerts all partners at once.',
    editNothingChanged: 'You have not changed anything yet.',
    editSaved: 'Changes saved.',
    phoneBreaksRule:
      'This saved number does not follow the phone number rule. Correct it with “Edit details”.',
    emailClearHint: 'Leave it empty to remove the email.',
    endsOn: 'Role ends on (optional)',
    endsOnHint:
      'For daily-wage and seasonal workers: access stops by itself. The role lasts until the end of the working day, the next morning, so a night shift is never cut off.',
    endsAtPreview: 'The role ends: {{when}}',
    endsAtFull: '{{date}}, {{time}} Baghdad time',
    endsAtLoading: 'Working out the end time…',
    endsAtFailed: 'The end time could not be worked out. Check the date.',
    endsOnLabel: 'Ends {{when}}',
    renew: 'Renew',
    setEnd: 'Set an end date',
    renewTitle: 'Renew the role “{{role}}”',
    setEndTitle: 'Set an end date for the role “{{role}}”',
    renewIntro:
      'Choose the new date. The exact end shows before saving, and the reason is kept in the audit log.',
    currentEnd: 'Ends now on: {{when}}',
    newEndsOn: 'New date',
    roleEndsSoon: 'A role ends {{when}}',
  },
  devices: {
    title: 'Devices',
    subtitle:
      'Computers registered at the branches, the warehouse and head office. Each is registered with a one-time code and can be revoked at any time.',
    newCode: 'New device code',
    empty: 'No devices registered at this location yet.',
    name: 'Device name',
    type: 'Type',
    typeHUB: 'Main PC of the location (hub)',
    typeDESKTOP: 'Computer (cashier or office)',
    typeKIOSK: 'Self-order kiosk',
    typePHONE: 'Phone',
    typeBACKUP_AGENT: 'Backup agent',
    typeHintHUB:
      'One dedicated PC per site, on a UPS. The other devices connect to it over the LAN.',
    typeHintDESKTOP: 'A cashier or office computer that works through the hub.',
    typeHintKIOSK: 'A screen where customers order by themselves inside the restaurant.',
    typeHintBACKUP_AGENT: 'The office computer that pulls an encrypted backup every night.',
    location: 'Location',
    status: 'Status',
    statusACTIVE: 'Active',
    statusREVOKED: 'Revoked',
    appVersion: 'Version',
    lastSeen: 'Last seen',
    never: 'Not yet',
    enrolledAt: 'Registered',
    revoke: 'Revoke device',
    revokeTitle: 'Revoke device “{{name}}”',
    revokeIntro:
      'Revoking is final: the device stops syncing at once and every session on it ends. To use it again it must be registered anew.',
    revokedBecause: 'Reason: {{reason}}',
    hubCertificate: 'LAN certificate fingerprint',
    connection: 'Connection / phone database',
    connectionHub: 'Through the main PC {{address}}',
    connectionServer: 'Straight to the server',
    connectionNone: 'No connection',
    connectionAt: 'Last report {{time}}',
    storeENCRYPTED: 'Database encrypted',
    storeMOVE_FAILED: 'Not moved to the encrypted database — sending from the old one',
    storeKEY_LOST: 'Database key missing',
    storeSTORE_UNREADABLE: 'Database does not open',
    storeFOLDER_UNREADABLE: 'Data folder could not be read',
    storeDetail: 'Reason: {{detail}}',
    photosWaiting: 'Photos not arrived yet: {{count}}',
    photosProblem: 'Photos not going since {{time}}',
    photosStep_link: 'asking the server for the upload link',
    photosStep_upload: 'uploading the photo to storage',
    photosStep_confirm: 'asking the server to confirm',
    hubCertificateMissing:
      'The hub has not registered its certificate yet (it does when it is online)',
    codeTitle: 'Registration code',
    codeIntro:
      'Type this code in the first setup screen of Dob Desktop on the new computer. It works once and will not be shown again.',
    codeExpires: 'Valid until {{time}} (30 minutes).',
    create: 'Create code',
  },
  printers: {
    title: 'Printers',
    titleAt: 'Printers — {{location}}',
    subtitle:
      'Network thermal printers of this location (ESC/POS, port 9100). Every change is kept in the audit log.',
    open: 'Printers',
    back: 'Back to locations',
    add: 'Add a printer',
    edit: 'Edit',
    editTitle: 'Edit "{{name}}"',
    remove: 'Remove from use',
    removeTitle: 'Remove "{{name}}" from use',
    removeIntro:
      'The printer disappears from use and cannot be brought back; it stays in the history. To use it again later, add it again.',
    history: 'History',
    name: 'Name',
    nameHint: 'A name people recognise, for example "Cashier 1" or "Kitchen".',
    host: 'IP address',
    hostHint:
      "As printed on the printer's self-test page, for example 192.168.1.50. Ask for a fixed address for it in the router.",
    port: 'Port',
    portHint: '9100 for almost every network printer.',
    address: 'Address',
    paper: 'Paper width',
    paper80: '80 mm',
    paper58: '58 mm',
    paperHint: 'The width of the paper roll. 80 mm unless the printer says otherwise.',
    use: 'Use',
    roleRECEIPT: 'Receipts',
    roleKITCHEN: 'Kitchen',
    roleOTHER: 'Other',
    inUse: 'In use',
    retired: 'Removed',
    retiredOn: 'Removed {{time}}: {{reason}}',
    pending: 'Waiting for the server',
    pendingHint: 'Changed on this site without internet; the server checks it when it arrives.',
    empty: 'No printers at this location yet.',
    showRetired: 'Removed printers ({{count}})',
    saved: 'Saved.',
    cannotManage:
      'You can see the printers here; adding or changing them needs the permission "Add, edit and remove a location\'s printers".',
    testPrint: 'Test print',
    testPrintWeb:
      'Test prints are sent from Dob Desktop at this location: the printer is on the branch network and cannot be reached from the internet.',
    desktopSubtitle:
      "This location's network printers. Anyone may look and print a test page; changes need the manager of this branch or IT, and work without internet too (the server checks them when it is reached).",
    accessTitle: 'Changing printers needs a sign-in',
    accessNeedSignIn:
      'Adding, changing or removing a printer needs the manager of this branch or IT signed in on this PC (with or without internet).',
    accessNotAllowed:
      '{{name}} may not change printers. The manager of this branch or IT is needed.',
    accessServerUnreachable:
      'The current sign-in cannot be confirmed without the server. Sign in without internet.',
    accessBy: 'Printers can be changed now: {{name}} ({{session}})',
    signIn: 'Sign in',
    routeself: 'Test prints from this PC are printed by this PC (the main PC).',
    routehub: 'Test prints from this PC go to the main PC, which prints them.',
    routealone: 'The main PC cannot be reached: this PC prints by itself.',
    testPrintSent: 'Test page sent to "{{name}}".',
    jobs: 'Recent print jobs',
    jobsEmpty: 'No print jobs yet.',
    jobWhen: 'Time',
    jobState: 'State',
    jobWhere: 'Queue',
    wherehere: 'This PC',
    wherehub: 'Main PC',
    statequeued: 'Waiting',
    stateprinting: 'Printing',
    statedone: 'Printed',
    statefailed: 'Failed',
    statecancelled: 'Cancelled',
    attempts: 'Tries: {{count}}',
    errUNREACHABLE:
      'The printer does not answer at its address (switched off, cable unplugged, or its address changed).',
    errCONNECTION_LOST: 'The printer dropped the connection while printing.',
    errTIMEOUT: 'The printer stopped taking data.',
    errPRINTER_REMOVED: 'This printer was removed from use.',
    errRENDER_FAILED: 'The page could not be prepared for printing.',
    errINVALID_DOCUMENT: 'The page is not valid.',
    retryJob: 'Try again',
    cancelJob: 'Cancel',
    errSIGN_IN_REQUIRED: 'Sign in as the manager of this branch or IT first.',
    errNOT_ALLOWED: 'This account may not change printers.',
    errSERVER_UNREACHABLE:
      'The sign-in cannot be confirmed without the server. Sign in without internet.',
    errPRINTER_NOT_FOUND: "This printer is not on this PC's list.",
    errNOT_REGISTERED: 'This PC is not registered yet.',
    notAvailable: 'Printers are available once this PC is registered to a location.',
  },
  messages: {
    title: 'Message log',
    subtitle:
      'Every WhatsApp, email and push message the system sent, with its status and attempts.',
    channels: 'Channels',
    channelWHATSAPP: 'WhatsApp',
    channelEMAIL: 'Email',
    channelPUSH: 'Phone push',
    transport: 'Transport: {{kind}}',
    transportStub: 'Stub — nothing is really sent',
    transportStubHint: 'Switched on when the service credentials are added on the server.',
    transportLive: 'Live',
    sendTest: 'Send a test message to me',
    testQueued: 'A test message was added. Its status: {{status}}.',
    testNoAddress:
      'No message was added: your account has no address for this channel (email or phone app).',
    filterStatus: 'Status',
    filterChannel: 'Channel',
    filterTemplate: 'Message type',
    empty: 'No messages match.',
    time: 'Time',
    recipient: 'Recipient',
    channel: 'Channel',
    template: 'Message type',
    status: 'Status',
    attempts: 'Attempts',
    details: 'Details',
    lastError: 'Last error',
    params: 'Content',
    statusQUEUED: 'Queued',
    statusSENT: 'Sent',
    statusDELIVERED: 'Delivered',
    statusFAILED: 'Failed',
    statusSTUBBED: 'Stub — not sent',
    statusSKIPPED: 'Skipped',
    templatepassword_reset_code: 'Password reset code',
    templatepartner_alert: 'Partner alert',
    templatetest_message: 'Test message',
    showing: 'Latest {{count}} messages',
  },
  health: {
    title: 'System health and backups',
    subtitle: 'Live check of the server every 30 seconds.',
    overall: 'Overall',
    allOk: 'Everything works',
    degraded: 'Something is down',
    unreachable: 'The server does not answer',
    services: 'Services',
    uptime: 'Running for',
    version: 'Version (git)',
    checkedAt: 'Last check',
    refresh: 'Check now',
    backups: 'Backups',
    backupsNotConfigured: 'Not set up on this server',
    backupsOk: 'Healthy',
    backupsWarning: 'Needs attention',
    backupsFailed: 'Problem',
    backupsSchedule:
      'An encrypted copy every night at 04:30 on the server and on Google Drive, and an automatic restore test on the 1st of every month. Any problem alerts the partners and IT.',
    lastBackup: 'Last backup',
    lastRestoreTest: 'Last restore test',
    officeCopy: 'Office PC copy',
    runOk: 'Succeeded',
    runFailed: 'Failed',
    failedStep: 'Failed at step: {{step}}',
    never: 'Never ran',
    unreadable: 'Cannot be read',
    googleDrive: 'Google Drive',
    driveok: 'Copy up to date',
    drivefailed: 'Copy failed',
    drivenot_configured: 'Not connected',
    contents: 'Contents',
    contentsValue: '{{tables}} tables, {{rows}} rows',
    snapshots: 'Copies kept',
    problems: 'What needs fixing',
    problembackup_missing: 'No backup has been taken yet.',
    problembackup_unreadable: 'The backup status file cannot be read.',
    problembackup_failed: 'The last backup failed.',
    problembackup_late: 'No backup for more than {{hours}} hours.',
    problemdrive_failed: 'Copying the last backup to Google Drive failed.',
    problemdrive_not_configured: 'Google Drive is not connected: the backup is on the server only.',
    problemrestore_test_missing: 'No restore test has run yet.',
    problemrestore_test_unreadable: 'The restore test status file cannot be read.',
    problemrestore_test_failed: 'The last restore test failed.',
    problemrestore_test_late: 'No restore test for more than {{days}} days.',
    problemoffice_copy_never: 'The office PC is registered but has never sent a copy report.',
    problemoffice_copy_failed:
      'The office PC’s last pull failed (the server and Google Drive copies are fine).',
    problemoffice_copy_late:
      'The newest backup on the office PC is more than {{hours}} hours behind the server’s last one.',
    problemoffice_incident_mass_deletion:
      'A large part of the backups disappeared from the server at once. The office PC keeps everything and deletes nothing until a partner allows it.',
    problemoffice_incident_config_replaced:
      'The repository config on the server is not the one the office PC holds; it kept its own.',
    problemoffice_incident_file_replaced:
      'The server lists a backup file with other content than the one the office PC verified; it kept its own.',
    problemoffice_incident_hash_mismatch:
      'A file received from the server failed its checksum and was refused.',
    problemoffice_incident_key_removed:
      'A key file was removed from the repository on the server; the office PC keeps its copy.',
    uptimeValue: '{{days}} d {{hours}} h {{minutes}} min',
    storageTitle: 'Disk and photos',
    diskOk: 'Enough space',
    diskLow: 'Space is running low',
    diskNotMeasured: 'Not measured on this server',
    diskFree: 'Free on the server',
    diskFreeValue: '{{free}} of {{total}}',
    photosStored: 'Photos kept',
    photosStoredValue: '{{photos}} photos, {{size}}',
    photosWaiting: 'Photos not arrived yet',
    storageHint:
      'The database, the photos and the nightly backups share the server’s disk. Below 20 GB (or a tenth of the disk) free, IT must act before the backups stop.',
  },
  backupAgent: {
    title: 'Office backup copy',
    subtitle:
      'This PC keeps the third copy of the backups. It copies the encrypted backup from the server every day; it never has the key.',
    updates: 'Updates',
    notAgent: 'This PC is not the office Backup Agent.',
    error_NOT_ABSOLUTE: 'Choose a folder on a disk of this PC (not a network folder).',
    error_NOT_A_LOCAL_DISK:
      'This folder is not on a local disk of this PC. Network and removable drives are not used: the copy must work when nobody is signed in.',
    error_NOT_WRITABLE:
      'Windows does not let this account write in that folder. Choose another one.',
    error_NOT_ENOUGH_SPACE: 'Not enough free space on that disk: at least 10 GB must stay free.',
    error_COPY_RUNNING: 'A copy is running. Wait until it ends.',
    error_NO_FOLDER: 'Choose the folder first.',
    error_NOT_BACKUP_AGENT: 'This PC is not the office Backup Agent.',
    error_DEVELOPMENT_BUILD: 'A development build cannot create the Windows task.',
    error_TASK_FAILED: 'Windows did not create the task. Try again, or ask IT.',
    error_other: 'It did not work ({{code}}).',
    folderTitle: 'Where should the copy be kept?',
    folderIntro:
      'Choose a disk of this PC with plenty of free space. At least {{min}} always stays free; the copy grows a little every day.',
    driveFree: '{{free}} free of {{total}}',
    enough: 'Enough space',
    notEnough: 'Less than {{min}} free',
    noDrives: 'No local disk found.',
    useSuggested: 'Use',
    pickFolder: 'Choose another folder…',
    folderHint:
      'The copy goes into the folder “staging” or “production” inside it. Changing the folder later starts a new copy there (the old one stays).',
    phase_manifest: 'Asking the server what is new…',
    phase_checking: 'Checking the files already on this PC…',
    phase_downloading: 'Copying…',
    phase_cleaning: 'Removing files the server deleted more than 30 days ago…',
    phase_reporting: 'Telling the server how it went…',
    progress: '{{done}} of {{total}} ({{files}} of {{filesTotal}} files)',
    lastFailed: 'The last copy stopped at: {{step}}.',
    step_manifest: 'asking the server (no internet?)',
    step_disk: 'disk space',
    step_download: 'download (it continues where it stopped next time)',
    step_verify: 'checksum',
    step_write: 'writing to the disk',
    step_cleanup: 'cleaning up',
    retryHint: 'It tries again by itself in 30 minutes.',
    lastOk: 'Last copy: {{time}}.',
    incidentsTitle: 'The copy refused something on its own',
    incident_MASS_DELETION:
      'A large part of the backups ({{files}} files, {{size}}) disappeared from the server at once. This PC keeps everything.',
    incident_CONFIG_REPLACED:
      'The backup’s configuration file changed on the server. This PC kept its own.',
    incident_FILE_REPLACED:
      'The server lists a file with other content than the one this PC checked. This PC kept its own.',
    incident_HASH_MISMATCH:
      'A file received from the server did not match its checksum and was refused.',
    incident_KEY_REMOVED: 'A key file was removed on the server. This PC keeps its copy.',
    incidentsHint:
      'The partners and IT have been alerted. A partner decides in the admin web → System health; nothing is changed here.',
    folder: 'Folder',
    lastSuccess: 'Last successful copy',
    never: 'Never',
    nextRun: 'Next copy',
    copyingNow: 'Copying now',
    scheduleHint: 'Every day at {{hour}}:00, and as soon as the PC is on when it was off then.',
    newest: 'Newest backup on this PC',
    size: 'The copy',
    sizeValue: '{{size}} ({{files}} files, {{snapshots}} copies)',
    free: 'Free space',
    kept: 'Deleted on the server, kept here',
    keptNone: 'Nothing',
    keptValue: '{{files}} files, {{size}} (kept 30 days)',
    pending: 'Reports waiting for the server',
    copyNow: 'Copy now',
    changeFolder: 'Change folder…',
    changeFolderHint: 'A new folder starts a new full copy.',
    taskTitle: 'Copy when nobody is signed in',
    task_always: 'Copies even when nobody is signed in',
    task_missing: 'Not set up yet',
    'task_signed-in-only': 'Copies only while someone is signed in',
    'task_without-password': 'Set up without the password',
    task_unknown: 'Cannot be read',
    'task_not-windows': 'Not on this PC',
    task_password: 'The Windows password changed',
    taskHelp_always: 'Windows starts the copy every hour and at sign-in; it copies only when due.',
    taskHelp_missing:
      'Create the Windows task: it starts the copy every hour and at sign-in (it copies only when due). Then switch it to “run whether the user is logged on or not” (docs/BACKUP_AGENT_AR.md).',
    'taskHelp_signed-in-only':
      'Now it copies only while someone is signed in to this PC. To copy at 06:00 with nobody signed in: open Task Scheduler → “Dob Backup Agent” → Properties → “Run whether user is logged on or not” → OK, and type this Windows account’s password in Windows’ own box (docs/BACKUP_AGENT_AR.md).',
    'taskHelp_without-password':
      '“Do not store password” is ticked: Windows then does not let Dob Desktop open its own protected files, so it cannot copy. Untick it in Task Scheduler and type the password.',
    taskHelp_unknown:
      'Windows did not say how the task is set up. Open Task Scheduler to check it.',
    'taskHelp_not-windows': 'The Windows task exists only on the installed Windows app.',
    taskHelp_password:
      'Windows could not start the copy: the account’s password changed. In Task Scheduler → “Dob Backup Agent” → Properties → OK, type the new password.',
    taskLastRun: 'Windows last started it: {{time}}',
    createTask: 'Create the Windows task',
    openTaskScheduler: 'Open Task Scheduler',
  },
  office: {
    title: 'Office copy (third backup copy)',
    notAvailable: 'Not available on this server',
    noAgent: 'No office PC yet',
    noAgentHelp:
      'IT registers the office PC in Devices with a “Backup Agent” code. The PC then pulls the encrypted backup every day; it never has the key.',
    pc: 'Office PC',
    lastSeen: '(last contact {{time}})',
    lastRun: 'Last run',
    error: 'What went wrong',
    lastSuccess: 'Last successful copy',
    newestHeld: 'Newest backup on the office PC',
    serverBackup: 'Server’s last backup: {{time}}',
    size: 'On the office PC',
    sizeValue: '{{size}} ({{files}} files, {{snapshots}} copies)',
    free: 'Free space on the PC',
    kept: 'Deleted on the server, kept here',
    keptNone: 'Nothing',
    keptValue: '{{files}} files, {{size}} (kept {{days}} days)',
    incident: 'Refused on its own',
    detected: 'Seen',
    concerns: 'Concerns',
    concernsValue: '{{files}} files, {{size}}',
    state: 'State',
    closed: 'Over',
    open: 'Still refused',
    partnerOnly: 'Waiting for a partner',
    release: 'Allow',
    releasedBy: 'Allowed by {{name}} at {{time}}',
    releaseTitle: 'Allow the office copy to go ahead',
    releaseHint: 'At least 10 characters: why this was expected. All partners are told.',
    releaseIntroMASS_DELETION:
      '{{files}} files ({{size}}) disappeared from the server at once, so the office PC kept them. Allow this only if you know why they were deleted (for example a planned clean-up). The files are then deleted on the office PC after the normal waiting days.',
    releaseIntroCONFIG_REPLACED:
      'The repository’s config file changed on the server, so the office PC kept its old one. Allow this only after a planned repository upgrade. The old file is kept aside on the office PC.',
    kindMASS_DELETION: 'Mass deletion on the server',
    kindCONFIG_REPLACED: 'Repository config changed',
    kindFILE_REPLACED: 'File replaced on the server',
    kindHASH_MISMATCH: 'Checksum does not match',
    kindKEY_REMOVED: 'Key removed on the server',
    stepmanifest: 'reading the list',
    stepdisk: 'disk space',
    stepdownload: 'download',
    stepverify: 'checksum',
    stepwrite: 'writing to disk',
    stepcleanup: 'clean-up',
    schedule:
      'The office PC pulls every day at {{hour}}:00 (and at start-up when it is behind). Files deleted on the server are kept {{days}} days; a sudden mass deletion is never followed without a partner.',
  },
  roles: {
    scopeALL: 'Company-wide',
    scopeLOCATION: 'One location',
    title: 'Roles and permissions',
    subtitle:
      'Each role is a set of permissions. What you change here applies at once to everyone holding the role, and all partners are told.',
    tabRoles: 'Roles',
    tabMatrix: 'Permission table',
    members: '{{count}} people',
    permissionsCount: '{{count}} permissions',
    twoFactor: 'Two-step verification',
    open: 'Open role',
    matrixHint: 'Each row is a permission and each column a role. ✓ means the role holds it.',
    has: 'Holds the permission',
    back: 'All roles',
    permissionsTitle: 'Permissions of this role',
    sensitive: 'Sensitive',
    sensitiveHint: 'A sensitive permission: power over other people, money or security.',
    offlineTitle: 'Allowed without internet',
    offlineNone: 'Nothing: this role does not work without internet.',
    offlineAt: 'On {{type}} devices: {{list}}',
    lockno_manage: 'View only: changing role permissions needs "Change role permissions".',
    lockpartner_role: 'Only a partner may change the partner role.',
    partnerNoRemove:
      'Permissions can be added to the partner role, never taken from it: partners see everything.',
    locknot_held: 'You do not hold it, so you cannot give or take it',
    locksegregated: 'Held by one role on purpose (separation of duties)',
    lockpartnerKeep: 'Never taken from the partner role',
    pending: 'Unsaved changes: {{add}} to add, {{remove}} to remove',
    reset: 'Undo',
    review: 'Review and save',
    confirmTitle: 'Save permissions of: {{name}}',
    confirmMembers:
      'The change applies at once to the {{count}} people holding this role, and all partners are told.',
    willAdd: 'Will be added',
    willRemove: 'Will be removed',
    reasonHint:
      "Why are this role's permissions changing? (at least 5 characters). Kept in the audit log and sent to the partners.",
    saved: 'The role permissions were saved.',
    notFound: 'This role does not exist.',
    modules: {
      users: 'People and roles',
      org: 'Branches and locations',
      devices: 'Devices and sessions',
      settings: 'Settings',
      approvals: 'Approvals',
      audit: 'Audit',
      emergency: 'Emergency Mode',
      ledger: 'Ledger books',
      cash: 'Cash boxes',
      sync: 'Sync',
      notifications: 'Messages',
      backups: 'Backups',
      inventory: 'Warehouse and items',
      purchasing: 'Purchasing and suppliers',
      production: 'Production',
      supply: 'Supplying branches',
      counts: 'Stock counts',
    },
  },
  account: {
    title: 'My account',
    profile: 'My details',
    sessions: 'My signed-in devices',
    sessionsIntro:
      'Devices and browsers where you are signed in. If you do not recognize one, end it and change your password.',
    thisDevice: 'This device',
    signedInAt: 'Signed in',
    lastUsed: 'Last used',
    ip: 'IP address',
    endSession: 'End session',
    unknownBrowser: 'Unknown browser',
  },
  inbox: {
    title: 'Notifications',
    empty: 'No notifications.',
    unreadOnly: 'Unread only',
    markRead: 'Mark as read',
    read: 'Read',
    severityINFO: 'Info',
    severityWARNING: 'Warning',
    severityCRITICAL: 'Urgent',
  },
  placeholder: {
    title: 'This screen is being built',
    body: 'It will be ready in the next part of Phase 1, after your feedback on this version.',
  },
  notFound: {
    title: 'Page not found',
    home: 'Back to home',
  },
  syncIssues: {
    title: 'Sync issues',
    subtitle:
      'Operations devices recorded offline that the server refused when they were sent. Nothing is dropped automatically: each issue waits for a person to decide.',
    filterStatus: 'Status',
    statusOPEN: 'Open',
    statusRESOLVED: 'Resolved',
    empty: 'No issues match.',
    occurredAt: 'When it happened',
    who: 'Employee and device',
    what: 'Operation',
    why: 'Why it was refused',
    category: 'Kind',
    categoryFINANCE: 'Financial',
    categoryOPERATIONS: 'Operations',
    open: 'Open',
    detailTitle: 'Sync issue',
    payload: 'Operation data as sent',
    location: 'Location',
    device: 'Device',
    noDevice: 'No device',
    resolution: 'Resolution',
    resolutionDISCARDED: 'Discarded',
    resolutionRETRIED_WITH_EDIT: 'Retried after a correction',
    resolutionCOMPENSATED: 'Compensating entry recorded',
    resolvedBy: 'Resolved by {{name}} at {{time}}',
    financeRule:
      'Financial issue: it cannot be closed directly. Write your proposal (discard, retry after a correction, or a compensating entry); the accountant approves it before it is carried out, and it appears in the daily partner report.',
    proposalOpen: 'A proposal for this issue is waiting for approval.',
    openProposal: 'Open the proposal',
    discard: 'Discard',
    retry: 'Correct and send again',
    proposeDiscard: 'Propose discarding',
    proposeRetry: 'Propose a correction',
    discardTitle: 'Discard the operation',
    discardIntro: 'The operation will never be carried out. The reason is kept in the audit log.',
    retryTitle: 'Correct the operation and send it again',
    retryIntro:
      'Correct the data and send it again. It runs under your name and permissions; if it is refused again, the issue stays open.',
    businessDate: 'Business date',
    businessDateHint: 'Optional: use it when, for example, the operation’s month is closed.',
    note: 'Reason',
    noteHint: 'Explain what happened and why this is the right fix (at least 10 characters).',
    invalidJson: 'This value is not valid JSON.',
    retried: 'The corrected operation was carried out and the issue is closed.',
    proposed: 'The proposal was sent to the accountant for approval.',
    proposeCompensate: 'Propose a compensating entry',
    compensateTitle: 'Propose a compensating entry',
    compensateIntro:
      'A compensating entry records in the books what really happened, instead of the refused operation. Example: the daily box was 5,000 IQD short → debit: cash differences under review 5,000, credit: daily box 5,000. The accountant approves it before it is recorded.',
    entryDate: 'Entry date',
    entryDateHint: 'Optional: left empty, it is dated the day it is approved.',
    compensationEntry: 'Open the compensating entry',
  },
  approvals: {
    title: 'Approvals',
    subtitle:
      'Sensitive requests that need approval before they are carried out (entry correction, closed period, cash difference, financial sync issue).',
    filterStatus: 'Status',
    filterType: 'Request type',
    empty: 'No requests match.',
    type: 'Request type',
    requestedBy: 'Requested by',
    requestedAt: 'Requested',
    reason: 'Reason',
    status: 'Status',
    approvals: 'Approvals needed',
    approvalsValue: '{{given}} of {{required}}',
    open: 'Open',
    statusPENDING: 'Waiting for a decision',
    statusAPPROVED: 'Approved — not carried out',
    statusREJECTED: 'Rejected',
    statusCANCELLED: 'Cancelled',
    statusEXECUTED: 'Carried out',
    back: 'All requests',
    detail: 'Request details',
    payload: 'Request content',
    decisions: 'Decisions',
    noDecisions: 'No decision yet.',
    decisionAPPROVE: 'Approve',
    decisionREJECT: 'Reject',
    result: 'Result',
    executedBy: 'Carried out by',
    cancelledBy: 'Cancelled by',
    lastError: 'Why carrying it out failed',
    lastErrorHint:
      'It was approved but carrying it out failed. Fix the cause and try again, or the requester cancels it.',
    approve: 'Approve',
    reject: 'Reject',
    decideTitle: 'Your decision',
    decideIntro:
      'Your decision is final and recorded under your name. All partners are told of every approval of a financial request.',
    comment: 'Comment',
    commentHintApprove: 'Optional when approving.',
    commentHintReject: 'Required when rejecting: explain why to the requester.',
    commentRequired: 'Write why you reject it.',
    executeAgain: 'Try carrying it out again',
    cancel: 'Cancel request',
    cancelTitle: 'Cancel the request',
    proposedEntry: 'Proposed entry',
    openEntry: 'Open the entry in the ledger',
    notYours: 'You cannot decide on a request you made yourself.',
  },
  ledger: {
    title: 'Ledger',
    subtitle:
      'Every dinar in or out is recorded here automatically. These screens are read-only: no entry is ever changed or deleted; a mistake is corrected by a reversing entry approved by a partner.',
    book: 'Book',
    bookHint:
      'The Company book covers Baghdad Road, the warehouse and head office. Each franchise has its own separate book.',
    noBooks: 'There are no books you can see.',
    tabJournal: 'Entries (journal)',
    tabAccounts: 'Accounts and balances',
    tabTrial: 'Trial balance',
    tabPeriods: 'Accounting months',
    tabVerify: 'Integrity check',
    howToRead: 'How to read an entry',
    howToReadBody:
      'Every money event is recorded on two equal sides: debit and credit. The simple rule: the debit is the account that received the money or value (the cash box when selling, the expense when paying), and the credit is the account that gave it (sales, or the cash box when paying an expense). Total debit always equals total credit in every entry.',
    debit: 'Debit',
    debitPlain: 'received',
    credit: 'Credit',
    creditPlain: 'gave',
    entryNo: 'Entry no.',
    date: 'Date',
    description: 'Description',
    amount: 'Amount',
    postedBy: 'Recorded by',
    postedAt: 'Recorded at',
    system: 'System',
    fromDate: 'From',
    toDate: 'To',
    show: 'Show',
    emptyJournal: 'No entries in this period.',
    loadOlder: 'Show older entries',
    reversedBadge: 'Reversed',
    reversalBadge: 'Reversal',
    emergencyBadge: 'Emergency Mode',
    sources: {
      cash_transfer_send: 'Cash handover (sent)',
      cash_transfer_receive: 'Cash received',
      cash_transfer_cancel: 'Cash handover cancelled',
      cash_transfer_resolution: 'Cash difference settled',
      ledger_adjustment: 'Correcting entry',
      ledger_opening_balance: 'Opening balance',
      ledger_reversal: 'Reversing entry',
      sync_issue_compensation: 'Compensating entry for a sync issue',
    },
    typeASSET: 'Assets',
    typeASSETHint: 'What the company owns: cash in boxes, e-wallet balances, stock, equipment.',
    typeLIABILITY: 'Liabilities',
    typeLIABILITYHint: 'What the company owes others: suppliers, salaries due.',
    typeEQUITY: 'Equity',
    typeEQUITYHint: 'The four partners’ capital, drawings and retained profit.',
    typeREVENUE: 'Revenue',
    typeREVENUEHint: 'What came in from sales, franchise royalties and other income.',
    typeEXPENSE: 'Expenses',
    typeEXPENSEHint: 'What was spent to run the restaurant: food, wages, rent, electricity…',
    code: 'Code',
    account: 'Account',
    balance: 'Balance',
    groupTotal: 'Total {{name}}',
    headerAccount: 'Group account',
    inactive: 'Inactive',
    openStatement: 'Statement',
    accountsHint:
      'Balances up to today, from every recorded entry. A group account shows the total of the accounts under it. Open any account to see its statement.',
    trialHint:
      'Every account with activity and its balance on the debit or credit side. When the two totals are equal, the books balance.',
    asOf: 'As of',
    location: 'Location',
    allLocations: 'All locations',
    totals: 'Total',
    balanced: 'Balanced: total debit = total credit',
    unbalanced: 'Not balanced! Tell the partners and IT at once.',
    emptyTrial: 'No activity up to this date.',
    statementTitle: 'Account statement',
    statementHint:
      'Every movement on this account in the chosen period, with the balance after each.',
    opening: 'Balance at the start (before {{date}})',
    closing: 'Balance at the end',
    balanceAfter: 'Balance after',
    periodTotals: 'Movements in the period',
    truncated:
      'Long period: only the first {{count}} movements are shown, and the last balance is the one after the last movement shown. Choose a shorter period to see the rest.',
    emptyStatement: 'No movements on this account in this period.',
    backToLedger: 'Ledger',
    balanceMeaningASSET: 'Balance = the amount held in this account.',
    balanceMeaningLIABILITY: 'Balance = the amount the company must pay.',
    balanceMeaningEQUITY: 'Balance = the partners’ share in this account.',
    balanceMeaningREVENUE: 'Balance = everything that came in (since records began).',
    balanceMeaningEXPENSE: 'Balance = everything spent (since records began).',
    headerNoStatement:
      'This is a group account; nothing is recorded on it directly. Open one of the accounts under it from the accounts list.',
    entryTitle: 'Entry no. {{no}}',
    lines: 'Lines',
    memo: 'Note',
    entryReason: 'Reason for the reversal or correction',
    sourceLabel: 'Kind of operation',
    reverses: 'This entry reverses entry no. {{no}}',
    reversedBy: 'Reversed by entry no. {{no}}',
    byApproval: 'Recorded after an approval',
    openApproval: 'Open the approval',
    emergencyNote:
      'Recorded during Emergency Mode. This is in the audit log and the partners were told.',
    immutable:
      'Entries are never changed or deleted. A mistake is corrected by a reversing entry approved by a partner, and both entries stay visible forever.',
    requestReverse: 'Request a reversal',
    reverseTitle: 'Request to reverse entry no. {{no}}',
    reverseIntro:
      'A reversal does not delete the entry: a new entry with the same amounts on the opposite sides is recorded, and both stay visible. The request goes to the partners for approval.',
    requestReason: 'Reason',
    requestReasonHint:
      'Explain the mistake clearly (at least 10 characters). The partners read it before deciding.',
    requestSent: 'The request was sent to the partners. Follow it on the approvals page.',
    sendRequest: 'Send the request',
    periodsHint:
      'Each month stays open for recording until it is closed. After reviewing a month the accountant closes it; nothing more is recorded in it without a partner’s approval. Months are closed in order, oldest first.',
    month: 'Month',
    periodOPEN: 'Open',
    periodCLOSED: 'Closed',
    closedBy: 'Closed by',
    notEnded: 'Not ended yet',
    closeMonth: 'Close the month',
    closeTitle: 'Close {{month}}',
    closeIntro:
      'After closing, no new entry dated in this month is accepted (late operations from devices appear as financial sync issues). Reopening needs a partner’s approval. Make sure the month was reviewed first.',
    closeDone: '{{month}} is closed.',
    closeEarlierFirst: 'Close the oldest month first',
    requestReopen: 'Request reopening',
    reopenTitle: 'Request to reopen {{month}}',
    reopenIntro:
      'The request goes to the partners. Once approved the month opens again for recording, and is then closed again.',
    verifyTitle: 'Book integrity check',
    verifyHint:
      'Every entry is sealed with a digital fingerprint linked to the entry before it. If anyone changes a number or deletes an entry directly in the database, this check finds it.',
    verifyNow: 'Check now',
    verifyIntact: 'Intact: {{count}} entries checked, nothing was changed.',
    verifyBroken:
      'Warning: entry no. {{no}} was changed or deleted after it was recorded. Tell the partners at once.',
    verifyNotAllowed:
      'This check is for people allowed to verify records (partners, audit manager).',
    addLine: 'Add a line',
    removeLine: 'Remove line {{n}}',
    lineN: 'Line {{n}}',
    chooseAccount: 'Choose the account…',
    accountRequired: 'Choose the account.',
    totalDebit: 'Total debit',
    totalCredit: 'Total credit',
    difference: 'Difference',
    linesBalanced: 'Balanced',
    lineAmountMissing: 'Enter the amount as a debit or a credit.',
    lineBothSides: 'A line is either a debit or a credit, not both.',
    linesTooFew: 'An entry needs at least two lines.',
    linesUnbalanced:
      'The entry does not balance: the difference is {{amount}}. Total debit and total credit must be equal.',
    amountInvalid: 'Enter the amount in digits only, in whole dinars (e.g. 25,000).',
    amountNotWhole: 'Amounts are whole dinars, no fractions.',
    amountTooLarge: 'The amount is too large.',
  },
  emergencyPage: {
    title: 'Emergency Mode',
    subtitle:
      "IT's exceptional right to correct financial data urgently, with the partners told at once.",
    whatItDoes:
      'When activated: all 4 partners are told at once (notification, WhatsApp, email), everything you do is flagged in the audit log, and it ends by itself after the chosen time. Any partner can end it at any moment.',
    activateTitle: 'Activate Emergency Mode',
    password: 'Password',
    code: 'Authenticator code',
    reason: 'Reason',
    reasonHint:
      'Explain what must be corrected and why it cannot wait for the partners’ approval (at least 20 characters).',
    minutes: 'Duration',
    minutesDefault: 'Default duration from settings',
    minutesValue: '{{count}} minutes',
    activate: 'Activate Emergency Mode',
    activeTitle: 'Emergency Mode is active now',
    activeLeft: 'Time left: {{time}}',
    endNow: 'End now',
    endTitle: 'End Emergency Mode',
    endIntro: 'A summary of everything done in it is sent to all partners.',
    note: 'Note',
    history: 'Emergency sessions',
    noHistory: 'Emergency Mode has never been activated.',
    startedAt: 'Started',
    activatedBy: 'Activated by',
    duration: 'Duration',
    state: 'State',
    stateActive: 'Active now',
    stateEARLY: 'Ended early',
    stateEXPIRED: 'Expired',
    endedBy: 'Ended by: {{name}}',
    actions: 'Actions recorded: {{count}}',
    seeActions: 'See what was done in the audit log',
    notForYou:
      'Activating Emergency Mode is for the IT admin. Here you can follow the sessions and end them.',
  },
  settingsPage: {
    title: 'Settings',
    subtitle:
      'The business rules the system uses. Every change is kept with its reason and who made it, and the old value is never erased: you can always tell which value applied at any time.',
    locationHint: 'Values of each branch follow the location chosen at the top: {{name}}.',
    chooseLocation: "To see or change a branch's own value, choose the branch at the top.",
    groups: {
      approval: 'Approvals',
      franchise: 'Franchise',
      auth: 'Sign-in and security',
      emergency: 'Emergency Mode',
      hub: 'Branches without internet',
      sync: 'Sync',
      other: 'Other',
    },
    current: 'In effect now',
    defaultValue: 'Default: {{value}}',
    usingDefault: 'Not changed yet (default value)',
    range: 'Allowed: {{min}} to {{max}}',
    rangeMin: 'Allowed: {{min}} or more',
    rangeMax: 'Allowed: up to {{max}}',
    lastChange: 'Last change: {{name}}, {{time}}',
    lastChangeReason: 'Reason: {{reason}}',
    companyValue: 'Company value',
    locationValue: "This branch's value",
    locationUsesCompany: 'This branch uses the company value.',
    scheduled: 'Scheduled change: {{value}} from {{time}}',
    sensitive: 'Partners are told',
    sensitiveHint: 'Every change to this setting is sent to all partners at once.',
    change: 'Change',
    changeHere: 'Change for this branch',
    history: 'History',
    noEditRight: 'You may not change this setting.',
    minutes: '{{count}} min',
    yes: 'Yes',
    no: 'No',
    changeTitle: 'Change setting',
    changeTitleHere: 'Change setting for: {{name}}',
    newValue: 'New value',
    percentHint: 'Type the percentage, e.g. 5 or 5.25',
    minutesHint: 'In minutes',
    startWhen: 'Starts to apply',
    startNow: 'Now',
    startLater: 'Later (Baghdad time)',
    startAt: 'Time (Baghdad time)',
    reasonHint:
      'Why is this setting changing? (at least 5 characters). Kept with the change and seen by the partners.',
    saved: 'The change was saved.',
    historyTitle: 'Change history: {{name}}',
    historyEmpty: 'This setting was never changed; the default value applies.',
    setAt: 'Changed at',
    startsAt: 'Starts',
    value: 'Value',
    where: 'Scope',
    who: 'Who',
    reason: 'Reason',
  },
  audit: {
    title: 'Audit log',
    subtitle:
      'Every change in the system: who made it, when, from which device, what changed and why. Nobody can edit or delete a line.',
    filters: 'Filters',
    from: 'From day',
    to: 'To day',
    entityType: 'Kind of record',
    action: 'Action',
    location: 'Location',
    anyValue: 'All',
    onlyEmergency: 'Only what was done in Emergency Mode',
    clearFilters: 'Clear all filters',
    activeFilters: 'Filters from another screen:',
    filterActor: 'Person: {{name}}',
    filterEntity: 'Record: {{id}}',
    filterSession: 'One Emergency session',
    remove: 'Remove',
    when: 'When',
    who: 'Who',
    what: 'What',
    reason: 'Reason',
    system: 'System (automatic)',
    device: 'From device: {{name}}',
    emergencyMark: 'Emergency Mode',
    notSealed: 'Not sealed yet',
    notSealedHint: 'A line is sealed into the protection chain a few seconds after it is written.',
    noRows: 'No lines match these filters.',
    loadMore: 'Show more',
    showDetails: 'Details',
    hideDetails: 'Hide details',
    field: 'Field',
    before: 'Before',
    after: 'After',
    changedFields: 'Fields changed: {{fields}}',
    showAllFields: 'Show all fields',
    showChangedOnly: 'Show only what changed',
    noData: 'No further data for this action.',
    redacted: 'Hidden (secret)',
    redactedChanged: 'Hidden (value changed)',
    empty: '—',
    recordId: 'Record id',
    requestIdLabel: 'Request id',
    ip: 'IP address',
    rowId: 'Audit line id',
    session: 'Emergency session',
    personActivity: "This person's activity",
    recordHistory: 'History of this record',
    actionINSERT: 'Added',
    actionUPDATE: 'Changed',
    actionDELETE: 'Removed',
    events: {
      auth_login: 'Signed in',
      auth_logout: 'Signed out',
      auth_password_reset: 'Password reset with a one-time code',
      auth_refresh_token_reuse: 'Old session reused (sessions ended)',
      auth_session_revoked: 'Session ended',
      ledger_entry_posted: 'Entry recorded',
      sync_command_refused: 'Sync command refused',
      auth_idle_sign_out: 'Signed out after inactivity on a shared PC',
      auth_phone_sessions_replaced: 'Earlier sign-in on the same phone ended',
      device_phone_registered: 'Phone registered',
      device_connection_changed: "PC's connection to the hub changed",
      backup_office_copy_manifest: 'Office backup copy pulled',
      attachment_view_link: 'View link for a money-document photo',
    },
    chainTitle: 'Integrity check',
    chainHint:
      'Each line is sealed together with the line before it, so a line changed or deleted directly in the database shows up here.',
    chainNotAllowed:
      'The integrity check is for people holding "Verify audit log integrity" company-wide.',
    chainCheck: 'Check now',
    chainIntact: 'The log is intact: no sealed line was changed or deleted.',
    chainBroken:
      'Warning: the chain is broken at line {{seq}}. Data was changed or deleted directly in the database. Tell the partners now.',
    chainSealed: 'Sealed lines: {{count}}',
    chainUnsealed: 'Waiting to be sealed: {{count}}',
    chainLatest: 'Fingerprint of the newest sealed line (no. {{seq}})',
    chainLatestHint:
      'Keep this fingerprint. Lines removed from the end of the log can only be found by comparing it with an earlier one; it will be sent daily in the partner report later.',
    chainCheckedAt: 'Checked at: {{time}}',
    entities: {
      users: 'Users',
      user_role_assignments: 'Role assignments',
      roles: 'Roles',
      role_permissions: 'Role permissions',
      permissions: 'Permission list',
      offline_policies: 'Offline permissions',
      locations: 'Branches and locations',
      setting_values: 'Setting values',
      setting_definitions: 'Setting definitions',
      devices: 'Devices',
      phone_store_losses: 'Phone databases that could not be opened',
      device_enrollment_codes: 'Device registration codes',
      sessions: 'Sign-in sessions',
      user_notifications: 'Notifications',
      ledger_books: 'Ledger books',
      accounts: 'Accounts',
      accounting_periods: 'Accounting months',
      journal_entries: 'Entries',
      cash_boxes: 'Cash boxes',
      cash_transfers: 'Cash handovers',
      cash_transfer_settlements: 'Cash difference settlements',
      approval_types: 'Approval types',
      approval_rules: 'Approval rules',
      approval_requests: 'Approval requests',
      approval_decisions: 'Approval decisions',
      emergency_sessions: 'Emergency sessions',
      sync_issues: 'Sync issues',
      sync_commands: 'Sync commands',
      sync_probes: 'Sync test records',
      push_tokens: 'Phone notification tokens',
      offline_verifiers: 'Offline sign-in data on PCs',
      printers: 'Printers',
      office_copy_reports: 'Office copy reports',
      office_copy_releases: 'Office copy go-aheads',
      item_categories: 'Item categories',
      items: 'Items',
      item_units: 'Purchase units',
      item_location_settings: 'Item settings per location (minimum level)',
      item_prices: 'Transfer prices',
      attachments: 'Photos',
      document_attachments: 'Document photos',
      stock_batches: 'Item batches',
      stock_movements: 'Stock movements',
      waste_documents: 'Waste documents',
      waste_lines: 'Waste lines',
      opening_documents: 'Opening balance documents',
      opening_stock_lines: 'Opening stock lines',
      document_corrections: 'Document corrections',
      suppliers: 'Suppliers',
      purchase_invoices: 'Purchase invoices',
      purchase_invoice_lines: 'Purchase invoice lines',
      goods_receipts: 'Goods receipts',
      goods_receipt_rows: 'Goods receipt count rows',
      goods_receipt_lines: 'Goods receipt results per invoice line',
      goods_receipt_damage_photos: 'Damage photos at receipt',
      purchase_excess_decisions: 'Decisions on receipt excess',
      photo_handoffs: 'Photos sent by phone (QR code)',
      photo_handoff_photos: 'Photos of a phone hand-off',
    },
  },
  emergency: {
    banner:
      'Emergency Mode is on — everything you do is recorded and reported to the partners. Ends in {{time}}.',
  },
  hub: {
    title: 'Hub status',
    subtitle: 'This PC is the main PC (hub) of {{location}}',
    link: 'Hub status',
    notHub: 'This PC is not the hub of its location.',
    back: 'Back',
    sending: 'Sending to the server',
    stateOnline: 'Connected to the server',
    stateOffline: 'No connection to the server: operations are kept and sent later',
    stateStarting: 'Starting',
    stateRevoked: 'This PC was cancelled by IT',
    ownPending: 'Entered on this PC, not sent yet',
    ownPendingHint:
      'These exist only on this PC until they are sent (do not switch it off before).',
    relayed: 'From other PCs, waiting to be sent',
    rejected: 'Refused by the server, need review',
    lastSync: 'Last successful sync',
    never: 'Not yet',
    lastError: 'Last problem',
    firstDownload:
      'Still downloading the data of the location for the first time; PCs use the server meanwhile.',
    lan: 'Local network',
    listening: 'PCs of the branch reach it at',
    notListening: 'Not reachable by the other PCs',
    port: 'Port',
    addresses: 'Addresses',
    noAddress: 'No network found',
    mdnsOn: 'Found automatically on the network',
    mdnsOff: 'Not announced on the network',
    fingerprint: 'Certificate fingerprint',
    fingerprintHint:
      'The other PCs accept this hub only with this fingerprint. IT can compare it with the Devices screen.',
    fingerprintRegistered: 'Registered with the server',
    fingerprintNotRegistered:
      'Not registered with the server yet (it will be, once there is internet)',
    windows: 'Windows network',
    profilePublic: 'Public',
    profilePrivate: 'Private',
    profileDomainAuthenticated: 'Company domain',
    profileUnknown: 'Unknown',
    firewall: 'Firewall rule',
    firewallPresent: 'Present (private networks)',
    firewallMissing: 'Missing',
    firewallDisabled: 'Switched off',
    firewallWrongProfile: 'Not for private networks',
    firewallUnknown: 'Could not be checked',
    addRule: 'Add the rule (needs an administrator)',
    ruleNotChanged:
      'The rule was not added (the administrator prompt was refused or Windows refused it).',
    recheck: 'Check again',
    checkedAt: 'Checked {{time}}',
    clients: 'PCs connected since the hub started',
    noClients: 'No PC has connected yet.',
    clientName: 'PC',
    clientAddress: 'Address',
    clientLastSeen: 'Last contact',
    unknownPc: 'Unknown PC',
    closingTitle: 'Waiting until everything is sent',
    closingBody:
      '{{count}} operations are still being sent. The program closes by itself when they are all sent.',
    keepWorking: 'Keep working',
    warnPublicTitle: 'The network of this PC is set to "Public"',
    warnPublicBody:
      'On a public network Windows blocks the other PCs from reaching the hub. Make it private: Settings → Network & internet → the network in use (Ethernet or Wi-Fi) → Network profile type → "Private network". The program never changes this itself.',
    warnFirewallTitle: 'The firewall rule for the hub is missing',
    warnFirewallBody:
      'Without it Windows may block the other PCs. Add it with the button below (Windows asks for an administrator).',
    warnPortTitle: 'Port {{port}} is used by another program',
    warnPortBody:
      'Close that program, or ask IT to choose another hub port for this branch (Settings → Hub port). The hub tries again every few seconds.',
    warnListenTitle: 'The hub could not open its network port',
    warnCertTitle: 'The certificate is not registered with the server yet',
    warnCertBody: 'It is registered automatically once there is internet.',
    warnMdnsTitle: 'The hub could not announce itself on the network',
    warnMdnsBody: 'The other PCs can still reach it by its address.',
    pairingTitle: 'Pair a PC with this main PC',
    pairingIntro:
      'Needed only when the main PC was replaced while the internet is down: type this code on the other PC, on its "Connection to the main PC" page. With internet, PCs pair automatically.',
    pairingShow: 'Show pairing code',
    pairingNew: 'New code',
    pairingHide: 'Hide the code',
    pairingExpires: 'Valid until {{time}}. Give it only to PCs of this branch.',
    offlineTitle: 'Sign-in without internet',
    offlineAccounts: 'People who can sign in without internet at this location',
    offlinePending: 'Sign-ins not reported to the server yet',
    offlineValidUntil: 'Their list stays valid without the server until {{time}}.',
    offlineNone:
      'The list has not come from the server yet (it comes at the first internet connection).',
    offlineHint:
      "This PC checks the password or PIN of this location's staff when the internet is down, for itself and for the other PCs of the branch.",
  },
  client: {
    title: 'Connection to the main PC',
    subtitle: 'How this PC reaches the main PC (hub) of {{location}}',
    notClient: 'This page is for ordinary PCs registered at a location.',
    linkHub: 'Through the main PC',
    linkServer: 'Straight to the server',
    linkAlone: 'Working alone',
    linkNotYourHub: 'Not your main PC',
    stateSearching: 'Looking for the main PC…',
    stateConnected: 'Connected to the main PC at {{address}}',
    stateNotPaired: "This PC does not know the main PC's certificate yet",
    stateNotPairedHint:
      'It learns it from the server automatically when there is internet. Without internet: pair it with the code shown on the main PC (below).',
    stateNotFound: 'The main PC was not found on the network',
    stateNotFoundHint:
      'Check that the main PC is on and on the same branch network, or type its address below.',
    stateNotYourHub: 'The PC at {{address}} is not your main PC',
    stateNotYourHubHint:
      'It presented another certificate, so nothing was sent to it. If the main PC was replaced: with internet the new one is learned automatically; without internet, pair this PC with the code shown on the new main PC.',
    stateNotReady: 'The main PC is still downloading the location data for the first time',
    stateUnreachable: 'The main PC does not answer',
    stateUnreachableHint: 'Check that it is on and that the network cable or Wi-Fi is connected.',
    stateRefused: 'The main PC does not accept this PC yet',
    stateRefusedHint: 'The main PC learns about new PCs when it connects to the internet.',
    fallbackServer: 'Operations now go straight to the server; nothing is lost.',
    fallbackAlone:
      'No internet and no main PC: this PC keeps its operations on its disk and sends them automatically when a connection is back.',
    sending: "This PC's operations",
    pending: 'Not yet on the server',
    atHub: '…of which held by the main PC',
    rejected: 'Refused by the server, need review',
    via: 'Last sent',
    viaHub: 'Through the main PC',
    viaServer: 'Straight to the server',
    viaNone: 'No connection now',
    firstDownload: 'Still downloading the location data for the first time.',
    trustedTitle: 'Trusted main PC',
    trustedNone: 'None yet',
    trustedFromServer: 'Certificate learned from the server',
    trustedFromPairing: 'Paired with a code on {{time}}',
    presented: 'Fingerprint of the PC that answered',
    findTitle: 'Finding the main PC',
    modeAuto: 'Automatically on the network',
    modeManual: 'At a typed address: {{address}}',
    address: "Main PC's address",
    addressHint: 'For example 192.168.1.10 or 192.168.1.10:8443. Port for this branch: {{port}}.',
    useAddress: 'Use this address',
    useAuto: 'Find automatically',
    searchAgain: 'Look again',
    found: 'Main PCs announced on the network for this location',
    foundNone: 'None so far.',
    foundTrusted: 'Trusted',
    foundOther: 'Another certificate',
    discoveryError: 'Could not search the network; you can type the address.',
    pairTitle: 'Pair with a code',
    pairIntro:
      'On the main PC: open "Main PC status" and press "Show pairing code", then type the code here. Not needed when there is internet.',
    pairCode: 'Pairing code (6 digits)',
    pairAddress: "Main PC's address (optional)",
    pairAddressHint: 'Leave it empty to look for it automatically.',
    pair: 'Pair',
    paired: 'Paired: this PC now trusts this main PC.',
    addressSaved: 'Address saved.',
    errINVALID_ADDRESS:
      'The address is not valid. Type for example 192.168.1.10 or 192.168.1.10:8443.',
    errINVALID_PAIRING_CODE: 'The pairing code has 6 digits.',
    errPAIR_NO_HUB_FOUND: 'The main PC was not found. Type its address in the second field.',
    errPAIR_UNREACHABLE: 'Nothing answers at this address.',
    errPAIR_NOT_A_HUB: 'The PC at this address is not a Dob main PC.',
    errPAIRING_CLOSED:
      'The main PC is not showing a pairing code now (or it expired). Press "Show pairing code" on it and try again.',
    errPAIR_UNKNOWN_DEVICE:
      'The main PC does not know this PC yet: it needs internet once after this PC was registered.',
    errPAIR_WRONG_CODE:
      'The code does not match. Check the code shown on the main PC and the address.',
    errPAIR_OTHER_LOCATION: 'This main PC belongs to another location.',
    errOther: 'That did not work. Try again.',
    accessTitle: 'Changing the connection needs a sign-in',
    accessNeedSignIn:
      'Pairing, typing the main PC address or finding it automatically needs the manager of this branch or IT signed in on this PC (with or without internet). The status above is visible to everyone.',
    accessNotAllowed:
      "{{name}} may not change this PC's connection. The manager of this branch or IT is needed.",
    accessServerUnreachable:
      'The current sign-in cannot be confirmed without the server. Sign in without internet.',
    accessSignIn: 'Sign in',
    accessBy: 'The connection can be changed now: {{name}} ({{session}})',
    sessionOnline: 'online',
    sessionOffline: 'without internet',
    errSIGN_IN_REQUIRED: 'Sign in as the manager of this branch or IT first.',
    errNOT_ALLOWED: "This account may not change this PC's connection.",
    errSERVER_UNREACHABLE:
      'The sign-in cannot be confirmed without the server. Sign in without internet.',
  },
  offline: {
    title: 'Sign in without internet',
    subtitle: 'For limited work on this PC only',
    intro:
      'The server does not answer. You can sign in on this PC without internet for limited work; the main PC or this PC checks it.',
    choose: 'Sign in without internet',
    backToOnline: 'Back to the normal sign-in',
    identifier: 'Phone number',
    identifierHint: 'Example: 07701234567',
    method: 'Sign in with',
    pin: 'PIN',
    password: 'Password',
    required: 'This field is required.',
    submit: 'Sign in without internet',
    checking: 'Checking…',
    r_bad_secret:
      'Wrong phone number or password / PIN. If you have not set a PIN yet, sign in with your password.',
    r_unknown_user:
      'Wrong phone number or password / PIN. If you have not set a PIN yet, sign in with your password.',
    r_locked: 'Too many wrong tries for this number. Try again after {{time}}.',
    r_device_locked: 'Too many wrong tries on this PC. Try again after {{time}}.',
    r_expired:
      'This PC has not reached the server for too long, so signing in without internet is no longer allowed. It needs the internet once.',
    r_not_allowed: 'Your account may not work without internet on this PC.',
    r_stale_secret:
      "The password or PIN changed after this PC's last contact. Sign in with internet once.",
    r_pin_not_allowed: 'Management accounts sign in with the password only, never a PIN.',
    r_password_too_short:
      'Yours is a management account: signing in without internet needs a password of at least 16 characters. Sign in online once and choose a longer password.',
    r_hub_needed:
      'This PC cannot check you by itself (you never signed in on it) and the main PC does not answer. Sign in when the main PC or the internet works.',
    r_new_device_not_allowed:
      'Signing in without internet is allowed only on a PC you signed in on before.',
    r_not_available: 'Signing in without internet is not available on this PC.',
    sessionOpen: 'You are signed in without internet as {{name}}.',
    continueOffline: 'Continue without internet',
    homeTitle: 'Working without internet',
    homeSignedIn: 'Signed in without internet: {{name}}',
    homeCan: 'What you can do without internet on this PC',
    allowed: 'Allowed',
    homeLimited:
      'Everything else (reports, administration, settings, month closing…) needs the internet.',
    homeLater:
      'The daily offline work screens (till, receiving…) come with their phases. Today: the connection to the main PC.',
    homeDetails: 'Sign-in details',
    homeMethod: 'Signed in with',
    homeCheckedBy: 'Checked by',
    checkedByHub: 'the main PC',
    checkedByPc: 'this PC',
    homeSince: 'Since',
    homeValidUntil: 'Signing in without internet stays valid on this PC until',
    backOnline: 'The server is reachable again.',
    signInOnline: 'Normal sign-in',
    signOut: 'Sign out',
  },
  updates: {
    title: 'Dob Desktop updates',
    link: 'Updates',
    installed: 'Installed version',
    channel: 'Versions this location receives',
    channelTest: 'Test versions (before everyone else)',
    channelStable: 'Stable versions',
    window: 'Update window',
    windowValue: '{{start}} – {{end}} (Baghdad time)',
    windowHint:
      'A new version is installed only in this window, after every operation of this PC is sent. A PC that was switched off during the window updates at its next start, before work.',
    now: 'Now',
    step_off: 'This PC does not update itself (a development build, or not registered yet).',
    step_paused:
      'Updates are paused for every PC (company setting “desktop_update.paused”). Nothing is downloaded or installed until IT lifts the pause.',
    step_checking: 'Looking for a new version…',
    step_up_to_date: 'Up to date.',
    step_not_yet:
      'Version {{version}} reaches this PC on {{time}} (a new version goes to some PCs first).',
    step_download: 'Downloading version {{version}}…',
    step_wait_window: 'Version {{version}} is ready. It will be installed at {{time}}.',
    step_send_first:
      'Sending the remaining operations ({{count}}) before installing version {{version}}…',
    step_install: 'Installing version {{version}}…',
    step_installing:
      'Installing version {{version}}. The program closes and opens again by itself.',
    progress: '{{percent}}% ({{done}} of {{total}} MB)',
    lastCheck: 'Last looked for a new version',
    never: 'Not yet',
    checkNow: 'Look for a new version now',
    checkNowHint: 'This only looks. Installing still waits for the window.',
    error_FEED_UNREACHABLE:
      'The downloads server could not be reached (usually: no internet). It is tried again automatically.',
    error_FEED_INVALID:
      'The version information on the downloads server is not valid. Nothing was installed. Tell IT.',
    error_SIGNATURE_INVALID:
      'The new version is not signed by Dob Burger, so it was not downloaded or installed. Tell IT at once.',
    error_DOWNLOAD_FAILED: 'The download stopped. It is tried again automatically.',
    error_CHECKSUM_MISMATCH:
      'The downloaded file is not the signed version, so it was not installed. Tell IT.',
    error_INSTALL_FAILED: 'The installer could not start. It is tried again at the next window.',
    startedWithout_offline:
      'At start-up the update could not be done (no internet, or operations could not be sent). The PC opened for work; the update is tried again at the next window or start-up.',
    startedWithout_too_slow:
      'At start-up the update took too long (slow internet). The PC opened for work; the download continues and the update is installed at the next window or start-up.',
    lastUpdate: 'Updated from version {{from}} to {{to}} on {{time}}.',
    beforeWorkTitle: 'Updating Dob Desktop before work',
    beforeWorkBody:
      'This PC was switched off during its update window. The update is done now, before work begins: first every operation is sent to the server, then the new version is installed. The program closes and opens again by itself.',
    beforeWorkDontClose: 'Do not switch off the PC.',
  },
  setup: {
    title: 'Set up this PC',
    subtitle: 'Only once, the first time Dob Desktop starts',
    stepOf: 'Step {{step}} of {{total}}',
    next: 'Next',
    back: 'Back',
    tryAgain: 'Try again',
    languageTitle: 'Choose the language',
    languageHint: 'You can change it later at the top of the screen.',
    serverTitle: 'Choose the server',
    serverHint:
      'This PC works with one server only. Changing it later means registering the PC again.',
    serverStagingHint: 'For testing and training. Its data is not real.',
    serverProductionHint: 'For real work in the branches.',
    serverDevelopmentHint: 'For the developer only, on this PC.',
    serverChecking: 'Contacting the server…',
    serverReachable: 'The server responds.',
    serverUnreachable:
      'The server does not respond. Check the internet and try again. Registration needs the internet only once.',
    codeTitle: 'Device registration code',
    codeHint:
      'IT creates the code in the admin web → Devices, for one location and one kind of device. It works once, for 30 minutes.',
    code: 'Code',
    codePlaceholder: 'XXXX-XXXX',
    codeChecking: 'Checking…',
    confirmTitle: 'Check the location and the mode',
    confirmHint: 'This comes from the code. If it is wrong, go back and ask for another code.',
    location: 'Location',
    mode: 'Mode of this PC',
    locationInactive: 'This location is not open in the system yet. The PC can be prepared now.',
    modeHintHUB:
      'Only one per location. It stays on during work, on a UPS, and serves the other PCs of the location over the local network, even without internet.',
    modeHintDESKTOP:
      'A cashier or office PC. It works through the main PC of the location, or directly with the server when the main PC is off.',
    modeHintBACKUP_AGENT:
      'An office PC that pulls an encrypted backup copy from the server every night and keeps it. No open port is needed.',
    kioskCode:
      'This code is for a self-order kiosk and cannot be used in this program. Go back and ask for a code for a PC.',
    warehouseTitle: 'Important: the only PC at the warehouse',
    warehouseBody:
      'The warehouse has one PC only: it is both the main PC and where data is entered. Operations entered on it have no second copy until they reach the server. So:',
    warehousePoint1:
      'Do not switch it off before every operation is sent. The program warns you at shutdown if some are not sent yet.',
    warehousePoint2: 'Connect it to a UPS so a power cut does not switch it off suddenly.',
    warehousePoint3:
      'Saved operations stay on the disk and are sent automatically at the next start with internet.',
    warehouseAgree: 'I have read and understood this warning',
    warehouseAgreeRequired: 'Tick “I have read and understood this warning” to continue.',
    pcName: 'Name of this PC',
    pcNameHint:
      'A name the staff know, such as “Cashier 1” or “Accountant PC”. It is shown on the Devices page.',
    register: 'Register this PC',
    registering: 'Registering…',
    doneTitle: 'This PC is registered',
    doneBody: 'This PC is registered as “{{name}}” — {{mode}} at {{location}}.',
    goToBackupAgent: 'Choose where the copy is kept',
    goToSignIn: 'Go to sign-in',
    revokedTitle: 'This PC’s registration was cancelled',
    revokedBody:
      'IT cancelled the registration of this PC (“{{name}}” at {{location}}), so it cannot be used until it is registered again. The cancellation was noticed at {{time}}.',
    unknownTitle: 'The server does not know this PC',
    unknownBody:
      'The server refused this PC’s key (“{{name}}” at {{location}}). The test server may have been reset. If this is unexpected, contact IT before registering again.',
    registerAgain: 'Register this PC again',
    registerAgainHint: 'You need a new registration code from IT.',
    notDesktop: 'The setup wizard exists only in Dob Desktop.',
    noProtection:
      'Windows cannot protect this PC’s key (encryption is not available for this account), so the PC cannot be registered.',
    alreadyRegistered:
      'This PC is already registered. To change the server, its registration must be cancelled first.',
  },
  inventory: {
    itemsTitle: 'Items',
    itemsSubtitle:
      'Every warehouse item: its stock unit, purchase units, minimum level at each location and the transfer price to branches. Quantities are always whole numbers (grams, millilitres, pieces).',
    categoriesTitle: 'Item categories',
    categoriesSubtitle:
      'Every item belongs to a category. The consumption account says where an item is costed when a branch consumes it.',
    addItem: 'Add item',
    addCategory: 'Add category',
    editItemTitle: 'Edit item: {{name}}',
    editCategoryTitle: 'Edit category: {{name}}',
    back: 'All items',
    searchLabel: 'Search',
    searchPlaceholder: 'Type part of the name or code',
    searchHint: 'Arabic letter variants (أ / ا / إ, ة / ه, ى / ي) are treated as the same.',
    categoryFilter: 'Category',
    allCategories: 'All categories',
    statusFilter: 'Status',
    showActive: 'Active',
    showInactive: 'Inactive',
    showAll: 'All',
    count: '{{count}} items',
    empty: 'No items yet.',
    emptySearch: 'No item matches the search.',
    notFound: 'This item does not exist.',
    cannotManage: 'View only: the warehouse manager adds and edits items.',
    code: 'Code',
    codeHint: 'Capital English letters, digits and dashes, e.g. DRK-PEPSI-330. Used in Excel.',
    name: 'Item',
    category: 'Category',
    stockUnit: 'Stock unit',
    stockUnitHint:
      'Chosen once and never changed: weight in grams (shown in kg), liquids in millilitres (shown in L), counted things in pieces.',
    stockUnitFixed: 'The stock unit never changes after the item is added.',
    unitG: 'Weight — gram (shown in kg)',
    unitML: 'Liquid — millilitre (shown in L)',
    unitPC: 'Count — piece',
    shortKG: 'kg',
    shortL: 'L',
    shortPC: 'pcs',
    purchaseUnits: 'Purchase units',
    purchaseUnitsHint:
      'Such as "Box = 24 pieces" or "Sack = 25 kg". The quantity in a unit never changes once saved; to change it, stop the unit and add another.',
    noUnits: 'No purchase units: bought in the stock unit itself.',
    addUnit: 'Add a purchase unit',
    unitNameAr: 'Unit name in Arabic',
    unitNameEn: 'Unit name in English',
    unitSize: 'How many {{unit}} in the unit?',
    unitSizeHint:
      'For example 24 for a box. Typed in kg or L for weights and liquids (25 = 25 kg).',
    unitOf: '{{name}} = {{quantity}} {{unit}}',
    defaultPurchase: 'Default purchase unit',
    defaultBadge: 'Default',
    unitRetired: 'Stopped',
    retireUnit: 'Stop this unit',
    restoreUnit: 'Use again',
    makeDefault: 'Make it the default',
    editUnitTitle: 'Edit unit: {{name}}',
    removeUnitRow: 'Remove this row',
    flags: 'Item properties',
    tracksBatches: 'Tracked by batch and expiry date',
    shelfLifeDays: 'Shelf life in days',
    shelfLifeHint:
      'For produced items: expiry = production date + this many days. Leave empty if not needed.',
    expiryAlertDays: 'Warn before expiry, in days',
    expiryAlertHint: 'Empty = the company setting (7 days).',
    isPurchased: 'Bought from suppliers',
    isProduced: 'Made in the warehouse (the mixer)',
    isSellableToBranches: 'Supplied to branches',
    isCountableForRecipes: 'Used in menu recipes (Phase 5)',
    notes: 'Notes',
    active: 'Active',
    inactive: 'Inactive',
    deactivate: 'Deactivate item',
    activate: 'Activate item',
    deactivateHint:
      'An inactive item stays in the records, but is hidden from the lists and gets no new price.',
    details: 'Details',
    minimums: 'Minimum level at each location',
    minimumsHint:
      'A "low stock" warning when the balance falls below it. A location that does not keep the item does not list or count it.',
    location: 'Location',
    minimum: 'Minimum',
    noMinimum: 'No minimum',
    stocked: 'Keeps it',
    notStocked: 'Does not keep it',
    editMinimum: 'Edit',
    minimumTitle: 'Minimum at {{location}}',
    minimumLabel: 'Minimum ({{unit}})',
    minimumFieldHint: 'Empty = no minimum.',
    isStockedLabel: 'This location keeps the item',
    testLocation: 'Automated test',
    price: 'Transfer price',
    pricesTitle: 'Transfer price to branches',
    pricesHint:
      'One price for every branch. A change starts now or at a later time you choose, never in the past. Every change is kept in the history with its reason.',
    noPrice: 'No price set yet',
    currentPrice: 'Current price',
    nextPrice: 'From {{time}}: {{price}}',
    perDisplay: 'per {{unit}}',
    perPurchaseUnit: 'per {{name}} ({{quantity}} {{unit}})',
    pricePer: '{{price}} {{per}}',
    changePrice: 'Change price',
    changePriceTitle: 'Transfer price: {{name}}',
    priceLabel: 'Price (dinars)',
    priceHint: 'Whole dinars, no fractions.',
    priceRequired: 'Enter the price in dinars.',
    perLabel: 'Price per',
    startLabel: 'Starts',
    startNow: 'Now',
    startLater: 'At a later time',
    startAt: 'Start time (Baghdad time)',
    priceReasonHint: 'Why does the price change? (at least 5 characters). Kept in the history.',
    historyTitle: 'Price history',
    historyEmpty: 'No prices yet.',
    effectiveFrom: 'Starts',
    recordedBy: 'Recorded by',
    reasonColumn: 'Reason',
    scheduled: 'Scheduled',
    inEffect: 'In effect',
    past: 'Past',
    history: 'History',
    saved: 'Saved.',
    priceSaved: 'The price was saved.',
    consumptionAccount: 'Consumption account at branches',
    consumptionHint:
      'When a branch consumes these items (from its counts, before the till system), their cost goes to this account.',
    consumption_food_cost: 'Food cost (5100)',
    consumption_packaging_cost: 'Packaging (5200)',
    consumption_cleaning: 'Cleaning and supplies (6700)',
    consumption_other_expenses: 'Other expenses (6900)',
    sortOrder: 'Order in lists',
    activeItems: '{{count}} active items',
    categoryActive: 'Active',
    categoryInactive: 'Inactive',
    deactivateCategory: 'Deactivate category',
    activateCategory: 'Activate category',
    categoriesEmpty: 'No categories yet. Add a category first, then the items.',
    quantityInvalid: 'Enter a number, for example 12.5',
    quantityDecimals: 'At most {{max}} digits after the decimal point.',
    quantityWhole: 'Pieces are whole numbers, without fractions.',
    quantityTooLarge: 'The number is too large.',
    unitSizeTooSmall: 'A unit must hold more than one {{unit}}.',
    photoTitle: 'Item photo',
    photoHint:
      'A clear photo of the item: JPEG, PNG or WebP. It is shrunk before upload and kept in private storage.',
    photoNone: 'No photo yet.',
    photoAlt: 'Photo of {{name}}',
    addPhoto: 'Add a photo',
    changePhoto: 'Change the photo',
    removePhoto: 'Remove the photo',
    photoUploading: 'Preparing and uploading the photo…',
    photoSaved: 'The photo was saved.',
    photoRemoved: 'The photo was removed from the item. It stays kept in the history.',
    photoNotImage: 'This file is not an image. Choose a JPEG, PNG or WebP photo.',
    photoTooBig: 'The photo is larger than 10 MB even after shrinking. Choose a smaller one.',
    photoUploadFailed:
      'The photo could not be uploaded to storage. Check the internet and try again.',
    photoLinkNote:
      'A photo link works for one minute only, and only signed-in people allowed to see the items get one.',
    photoLoadFailed: 'The photo could not be shown. Refresh the page.',
  },
  purchases: {
    suppliersTitle: 'Suppliers',
    suppliersSubtitle:
      'The companies and people we buy from. Names never repeat, so the right supplier is picked from the list.',
    newSupplier: 'New supplier',
    editSupplier: 'Edit supplier',
    supplierNameAr: 'Supplier name (Arabic)',
    supplierNameEn: 'Supplier name (English, optional)',
    contactPerson: 'Contact person',
    phones: 'Phone numbers',
    phonesHint: 'Up to 3 numbers, separated by commas.',
    address: 'Address',
    category: 'Category',
    categoryHint: 'For example: drinks, meat, packaging.',
    notes: 'Notes',
    paymentTerms: 'Payment terms (days)',
    paymentTermsHint: 'How many days after the invoice date a credit invoice is due. 0 = at once.',
    termsDays: '{{n}} days',
    termsNow: 'At once',
    active: 'Active',
    inactive: 'Stopped',
    stopSupplier: 'Stop supplier',
    reactivateSupplier: 'Reactivate supplier',
    searchSuppliers: 'Search by name, number or category',
    noSuppliers: 'No suppliers yet.',
    showInactive: 'Show stopped ones',
    backToSuppliers: 'All suppliers',
    supplierInvoices: "This supplier's invoices",
    changeReason: 'Reason for the change',
    phoneSharedNote: 'About the numbers',
    invoicesTitle: 'Purchase invoices',
    invoicesSubtitle:
      'Every invoice with a photo of its paper. Never edited once recorded: it is cancelled with a reason and recorded again, and the cancelled one stays visible.',
    newInvoice: 'New invoice',
    noInvoices: 'No invoices yet.',
    statusRECORDED: 'Recorded',
    statusRECEIVED: 'Received',
    statusCANCELLED: 'Cancelled',
    allStatuses: 'All statuses',
    allSuppliers: 'All suppliers',
    location: 'Warehouse',
    chooseLocation: 'Choose the warehouse.',
    supplier: 'Supplier',
    chooseSupplier: 'Choose the supplier.',
    searchSupplier: 'Search for the supplier',
    number: 'Invoice number',
    numberRequired: 'Type the invoice number as it is on the paper.',
    numberHint: 'As on the paper. The same supplier and number are never recorded twice.',
    invoiceDate: 'Invoice date',
    dateRequired: 'Choose the invoice date.',
    paymentKind: 'Payment',
    paymentCREDIT: 'Credit',
    paymentCASH: 'Cash',
    cashHint: 'A cash invoice is due at once; the accountant pays it from the safe (step W6).',
    dueDate: 'Due date',
    dueDateHint: "Leave empty: the invoice date plus the supplier's terms.",
    dueBeforeInvoice: 'The due date is before the invoice date.',
    discount: 'Invoice discount (IQD)',
    discountHint: 'Spread over the lines by their value, so the total still matches to the dinar.',
    discountTooLarge: 'The discount is more than the lines add up to.',
    note: 'Note',
    lines: 'Invoice lines',
    addLine: 'Add a line',
    removeLine: 'Remove the line',
    chooseItem: 'Choose the item.',
    unit: 'Unit',
    chooseUnit: 'Choose an active unit of this item.',
    perDisplay: 'per {{unit}}',
    quantityInUnit: 'Quantity',
    quantityRequired: 'Type the quantity.',
    quantityInvalid: 'Type the quantity in numbers (up to 3 decimals).',
    quantityNotWhole: 'This quantity does not make a whole number of the smaller unit. Correct it.',
    price: 'Price per unit (IQD)',
    priceRequired: 'Type the price.',
    priceWhole: 'Prices are whole dinars, no fractions.',
    priceInvalid: 'Type the price in numbers.',
    lineTotal: 'Line total',
    grossTotal: 'Lines total',
    total: 'Total',
    priceJumpReason: 'Reason for the higher price',
    priceJumpReasonRequired:
      'The price is above the last one by more than the limit. Write the reason (at least 5 characters).',
    priceJumpHint:
      'More than {{limit}}% above the last price. Last price: {{price}} per {{unit}} — invoice "{{number}}" of {{date}} from "{{supplier}}".',
    lastPriceHint: 'Last price: {{price}} per {{unit}} ({{date}})',
    photos: 'Invoice photos',
    photoRequired:
      'At least one photo of the invoice paper. Once recorded, it is never removed or replaced.',
    addPhoto: 'Add a photo',
    recordInvoice: 'Record the invoice',
    recordInvoiceTitle: 'New purchase invoice',
    replaces: 'Replaces the cancelled invoice',
    replacesThis: 'This invoice is recorded in place of the cancelled invoice "{{number}}".',
    recordedBy: 'Recorded by',
    cancelInvoice: 'Cancel the invoice',
    cancelTitle: 'Cancel invoice "{{number}}"',
    cancelIntro:
      'A cancelled invoice stays visible for ever, with its photos, your reason and your name. You can then record it again with the same number.',
    cancelReasonHint: 'Write the reason clearly (at least 10 characters).',
    cancelledNotice: 'Cancelled by {{name}} on {{time}}. Reason: {{reason}}',
    replacedBy: 'Recorded again as invoice "{{number}}".',
    replacesNotice: 'Replaces the cancelled invoice "{{number}}".',
    recordAgain: 'Record it again',
    addPhotoTitle: 'Add a photo to the invoice',
    addPhotoIntro: 'Once recorded, photos are only added, never removed. Write why you add it.',
    photoAddedLater: 'Added later ({{name}}): {{reason}}',
    fullSizeHint: 'Click a photo to open it at full size and read the small numbers.',
    detailTitle: 'Invoice "{{number}}"',
    backToInvoices: 'All invoices',
    priceJumped: 'Higher price',
    notReceivedYet:
      'The goods are not received yet. Nothing is in stock or in the books until the receipt.',
    linesCount: '{{n}} lines',
  },
  // W5-6: the photo with the phone through a QR code (the page's side).
  handoff: {
    start: 'Take it with the phone',
    scanTitle: 'Take the photo with your phone through this code',
    step1: 'Open Dob Staff on your phone.',
    step2: 'In «Work» choose «Photo for the PC» and point the camera at this code.',
    step3: 'Photograph the whole paper, or its parts if it is long. The photos appear here.',
    validFor: 'The code works for {{minutes}}:{{seconds}}',
    expired: 'The code expired and no photo arrived.',
    newCode: 'New code',
    arrived: 'Photos arrived from the phone: {{n}}',
    moreHint: 'You can photograph another part with the same code while it works.',
    confirmedTitle: 'Marked: the photos arrived from the phone',
    confirmedHint:
      "This PC has no internet: the phone's photos are linked to the document when it reaches the server. If they had not arrived, the document is refused with that reason and nothing of it is lost.",
    offlineTitle: 'This PC cannot reach the server now',
    offlineHint: 'When the phone says «Arrived», press «The photo arrived».',
    confirm: 'The photo arrived',
    cancel: 'Cancel the phone photo',
    drop: "Don't use the phone's photos",
  },
  receiving: {
    tasksTitle: 'Receive goods',
    tasksSubtitle:
      "Invoices waiting for their goods. Count what really arrived: the invoice's quantities and prices are not shown to you.",
    noTasks: 'No invoice is waiting to be received.',
    recordedBySelf: 'You recorded this invoice, so someone else receives its goods.',
    receive: 'Receive',
    linesToCount: '{{n}} item(s) to count',
    backToTasks: 'Everything waiting to be received',
    receiveTitle: 'Receive invoice "{{number}}"',
    blindIntro:
      "Count each item as it arrived, and type 0 if none of it came. The invoice's quantities and prices are not shown here; the system compares after you save.",
    closedTask: 'This invoice is no longer waiting to be received.',
    received: 'Arrived',
    damaged: 'Of which damaged',
    damagedHint: 'Leave empty if nothing is damaged.',
    countIn: 'Counted in',
    batchCode: 'Batch code (optional)',
    batchCodeHint: 'As on the pack. If you leave it empty, the system makes one.',
    productionDate: 'Production date (optional)',
    expiryDate: 'Expiry date',
    addBatch: 'Another batch of the same item',
    removeRow: 'Remove this row',
    damagePhotoRequired: 'Photograph the damaged goods: at least one photo.',
    addDamagePhoto: 'Photograph the damage',
    unlistedTitle: 'Goods that arrived but are not on the invoice',
    unlistedHint:
      'Kept aside, outside stock, until the warehouse manager decides: an extra invoice or a return.',
    addUnlisted: 'Add an item that is not on the invoice',
    note: 'Note (optional)',
    confirm: 'Save the receipt',
    confirmTitle: 'Save the receipt',
    confirmIntro: 'Once saved, the receipt is not edited. Check the counts and the damage photos.',
    done: 'The receipt is recorded. Thank you.',
    doneHint:
      'The system compares what you counted with the invoice, and tells the warehouse manager if something is in excess.',
    receivedRequired: 'Type how much arrived (0 if nothing came).',
    quantityInvalid: 'Type the count in digits (up to 3 decimals for kg and L).',
    quantityNotWhole: 'This does not make a whole number of pieces. Correct it.',
    damagedTooMany: 'More damaged than arrived.',
    expiryRequired: "Type this batch's expiry date.",
    datesOrder: 'The production date is after the expiry date.',
    chooseItem: 'Choose the item.',
    unlistedQuantity: 'Type how much of it arrived.',
    receiptTitle: 'Receipt',
    receivedByAt: 'Received by {{name}} on {{time}}',
    invoiced: 'Invoiced',
    accepted: 'Into stock',
    claim: 'Short or damaged',
    excess: 'Excess',
    acceptedValue: 'Value into stock',
    claimValue: 'Claim on the supplier',
    claimHint:
      'Short and damaged goods do not enter stock: their value is owed back by the supplier on "Supplier claims" until settled (step W6).',
    rowsTitle: 'What was counted, row by row',
    excessLeft: 'Waiting for a decision: {{n}}',
    excessWaitingLink: 'Open the receipt differences to decide',
    fromExcessOf: 'Extra invoice for the excess received with invoice "{{number}}"',
    extraInvoiceLink: 'Extra invoice "{{number}}"',
    returnLine: 'Returned {{quantity}} — {{name}}, {{time}}: {{reason}}',
    noteLabel: "Receiver's note",
    excessTitle: 'Receipt differences',
    excessSubtitle:
      'Goods that arrived beyond the invoice. They stay out of stock until you decide: an extra invoice from the supplier, or a return with a signed paper.',
    noExcess: 'No excess is waiting for a decision.',
    waitingQuantity: 'Waiting',
    receivedAt: 'Received at',
    returnExcess: 'Return',
    extraInvoice: 'Extra invoice',
    returnTitle: 'Return the excess of "{{item}}"',
    returnIntro:
      "A return closes this excess without it entering stock. Photograph the return paper the supplier's driver signed.",
    returnQuantity: 'Quantity returned ({{unit}})',
    returnQuantityHint: 'Leave empty to return all that is left ({{n}}).',
    returnReason: 'Reason',
    returnReasonHint: "For example: the supplier's driver took it back (at least 10 characters).",
    returnPhotoRequired: 'At least one photo of the signed return paper.',
    addReturnPhoto: 'Photograph the return paper',
    extraIntro:
      'An extra invoice for the excess received with invoice "{{number}}". It is received at once from the excess already counted, and follows every rule of an invoice: a photo of the paper, a number never used twice, and a reason if the price rose.',
    extraNoExcess: 'Nothing of this receipt’s excess is waiting for a decision.',
    extraConfirmTitle: 'Check the extra invoice before saving it',
    extraConfirmWarning:
      'This invoice cannot be cancelled once saved, and it is received at once: its goods enter stock and its amount is owed to the supplier. Check the supplier, the items, the quantities and the total.',
    extraConfirmSave: 'Confirm and save',
    extraConfirmBack: 'Back to edit',
    statementTitle: 'Supplier statement',
    statementHint:
      'Everything we owe this supplier and everything we claim from them, read from the entries themselves. Payments and aging come with step W6.',
    owed: 'Owed to the supplier',
    claims: 'Claims on the supplier',
    noStatement: 'No entries for this supplier yet: they are posted when goods are received.',
    date: 'Date',
    entry: 'Entry',
    invoice: 'Invoice',
    accountPAYABLE: 'Suppliers payable (2100)',
    accountCLAIMS: 'Supplier claims (1360)',
    debit: 'Debit',
    credit: 'Credit',
    runningOwed: 'Owed after',
    runningClaims: 'Claims after',
  },
  pcPurchases: {
    title: 'Purchasing on this PC',
    intro:
      'An invoice and a receipt are saved with their photos on this PC first, in one step, then sent when there is internet, once.',
    newInvoice: 'New invoice on this PC',
    invoicesHere: 'Invoices recorded on this PC',
    invoicesHereEmpty: 'No invoice recorded on this PC yet.',
    receiptsHere: 'Receipts recorded on this PC',
    receiptsHereEmpty: 'No receipt recorded on this PC yet.',
    tasksHere: 'Invoices waiting for their goods',
    savedInvoice:
      'The invoice and its photos are saved on this PC. They are sent when there is internet, once.',
    savedReceipt: 'The receipt is saved on this PC. It is sent when there is internet, once.',
    alreadySaved: 'This was saved on this PC before, and was not saved a second time.',
    stateWaiting: 'Waiting to be sent',
    stateArrived: 'Arrived',
    stateRefused: 'Refused by the server: {{reason}}',
    cancelWaiting: 'Cancel waiting to be sent',
    cancelArrived: 'Cancelled',
    cancelRefused: 'Cancel refused: {{reason}}',
    cancelHere: 'Cancel',
    cancelHereTitle: 'Cancel invoice "{{number}}"',
    cancelHereIntro:
      'An invoice is cancelled only before its goods are received. If they were received on another device, the server refuses the cancel and the reason shows here.',
    cancelReason: 'Reason for cancelling',
    keepInvoice: 'Back, do not cancel',
    pickPhoto: 'Choose a photo from this PC',
    photoHintInvoice:
      'At least one photo of the invoice paper. The warehouse PC has no camera: choose a photo saved on it.',
    photoHintDamage: 'Photograph the damaged goods, then choose the photo from this PC.',
    photosWaiting: 'Photos not sent yet: {{n}}',
    photosRefused: 'Photos refused by the server: {{n}} — tell IT',
    lines: 'Items: {{n}}',
    backToList: 'Back to the list',
    unknown_supplier: "This supplier is not in this PC's copy.",
    inactive: 'This supplier is stopped.',
    date_in_future: 'The invoice date is after today.',
    invoice_number_taken: "This supplier's invoice number is already recorded on this PC.",
    unknown_item: "This item is not in this PC's copy.",
    not_purchased: 'This item is not bought (stopped, or not a purchased item).',
    invalid_choice: 'This unit is not an active unit of this item.',
    price_jump_reason_required:
      'The price is above the last price by more than the limit: write the reason.',
    discount_too_large: 'The discount is more than the lines add up to.',
    not_image: 'This file is not a photo.',
    not_on_this_pc: 'Only invoices recorded on this PC are cancelled from here.',
    already_received: "This invoice's receipt is already recorded on this PC.",
    not_waiting: 'This invoice is not waiting for a receipt here.',
    second_person: 'You recorded this invoice, so another person receives its goods.',
    line_missing: 'You did not say how much of every item arrived. Type 0 if nothing came.',
    damaged_more_than_received: 'More damaged than received.',
    other_item: 'This row does not match the invoice.',
    unknown_line: 'This row does not match the invoice.',
    expiry_required: 'This item is kept by batch: type the expiry date.',
    dates_order: 'The production date is after the expiry date.',
    batch_not_used: 'This item has no batches.',
    damage_photo_required: 'Photograph the damaged goods: at least one photo.',
    invalid: 'Could not save: check the fields.',
    NOT_A_WAREHOUSE: 'Purchase invoices are recorded on a PC at a warehouse only.',
    SAVE_FAILED: 'Could not save on this PC, and nothing was saved. Try again.',
  },
  stock: {
    wasteTitle: 'Waste',
    wasteSubtitle:
      'Everything that leaves stock damaged, expired or spoiled, with its photo and value.',
    recordWaste: 'Record waste',
    recordWasteTitle: 'Record waste',
    wasteEmpty: 'No waste recorded yet.',
    wasteRecorded: 'The waste is recorded.',
    reason: 'Reason',
    reasonEXPIRED: 'Expired',
    reasonDAMAGED: 'Damaged',
    reasonSPOILED: 'Spoiled',
    reasonOTHER: 'Other',
    reasonText: 'Describe the reason',
    note: 'Note (optional)',
    location: 'Location',
    chooseLocation: 'Choose the location',
    allLocations: 'All locations',
    item: 'Item',
    searchItem: 'Search an item by name or code',
    chooseItem: 'Choose the item',
    noItemFound: 'No item by that name.',
    batch: 'Batch',
    chooseBatch: 'Choose the batch',
    batchOption: '{{code}} — expires {{expiry}}',
    quantity: 'Quantity ({{unit}})',
    quantityHeader: 'Quantity',
    quantityRequired: 'Type the quantity.',
    moreThanAvailable: 'More than is available at this location.',
    addLine: 'Add an item',
    removeLine: 'Remove the line',
    lineNo: 'Line {{n}}',
    photos: 'Photos',
    addWastePhoto: 'Add a photo',
    photoRequired: 'At least one photo: no photo, no waste.',
    photoUploading: 'Uploading the photo…',
    photoReady: 'Photo {{n}} ready',
    photoNotArrived: 'The photo has not arrived from the device yet',
    photoNotArrivedHint: 'The device it was taken on sends it by itself when it is online.',
    value: 'Value',
    total: 'Total',
    recordedBy: 'Recorded by',
    date: 'Date',
    items: 'Items',
    linesCount: '{{n}} line(s)',
    back: 'Back',
    documentEntries: 'Journal entries',
    documentEntriesHint:
      "This document's entry in the books, and its correction's entries if it was corrected.",
    entryNo: 'Entry no. {{n}} — {{date}}',
    entryReverses: 'Reverses entry no. {{n}}',
    noEntry: 'No entry: the goods had no value yet.',
    account: 'Account',
    debit: 'Debit',
    credit: 'Credit',
    corrected: 'Corrected',
    correctedNote:
      'This document was corrected on {{time}} with a partner’s approval. Reason: {{reason}}',
    replacedBy: 'The new document',
    replaces: 'This document replaced a corrected one.',
    openOriginal: 'The original',
    isReplacement: 'Replacement',
    requestCorrection: 'Request a correction',
    correctionTitle: 'Correct a posted document',
    correctionIntro:
      'A posted document is never edited. It is reversed at its original values — and the correct one recorded in its place, if any — after a partner approves. Both stay visible for ever.',
    correctionReason: 'Why it must be corrected (at least 10 characters)',
    reverseOnly: 'Reverse it only (this waste did not happen)',
    reverseAndReplace: 'Reverse it and record the correct one',
    reverseOpening: 'Reverse this opening stock (then prepare a new one)',
    replacementLines: 'The correct waste',
    reusePhotos: 'The original’s photos are used; you can add more.',
    correctionSent: 'The correction request was sent. It waits for a partner.',
    pendingCorrection: 'This document has a correction waiting for approval.',
    openRequest: 'Open the request',
    openDocument: 'Open the document',
    sendRequest: 'Send the request',
    openingsTitle: 'Opening stock',
    openingsSubtitle:
      'What the warehouse or branch holds the day it starts using the system: item, quantity and value. Posted only with a partner’s approval.',
    newOpening: 'New opening stock',
    openingsEmpty: 'No opening stock yet.',
    status: 'Status',
    statusDRAFT: 'Draft',
    statusCANCELLED: 'Cancelled',
    statusPOSTED: 'Posted',
    statusREVERSED: 'Reversed',
    businessDate: 'Date of the stock',
    unitCost: 'Cost per {{unit}} (IQD)',
    unitCostRequired: 'Type the cost in dinars (0 or more).',
    cost: 'Cost (IQD)',
    costPer: 'Per',
    lineValue: 'Line value',
    batchCode: 'Batch code',
    productionDate: 'Production date (optional)',
    expiryDate: 'Expiry date',
    batchNeeded: 'This item is kept by batch: type the batch code and its expiry date.',
    draftSaved: 'The draft is saved. Check it, then ask for posting.',
    requestPosting: 'Ask for posting',
    postingTitle: 'Ask for the opening stock to be posted',
    postingIntro:
      'Another partner approves it. Once approved, the stock comes in and the entry is posted: Dr inventory / Cr Opening balances.',
    postingReason: 'A note for the partner (at least 10 characters)',
    postingSent: 'The posting request was sent. It waits for a partner.',
    cancelDraft: 'Cancel the draft',
    cancelReason: 'Why it is cancelled (at least 5 characters)',
    draftCancelled: 'The draft is cancelled.',
    preparedBy: 'Prepared by',
    onePerLocation:
      'One posted opening stock per location. If it is wrong, ask for its correction, then prepare a new one.',
    waitingApproval: 'Waiting for a partner',
    stockTitle: 'Stock',
    hereTitle: 'Stock by location',
    hereSubtitle:
      'What each location holds now of every item, with its batches. Values for those who may see costs.',
    hereSubtitleDesktop:
      "This PC's copy of the server's data for this location — works without internet. Values for those who may see costs, in a normal sign-in only.",
    hereTitleAt: 'Stock at {{location}}',
    hereEmpty: 'No stock at this location yet.',
    hereNoMatch: 'No item matches the search.',
    provisional: 'Provisional',
    provisionalHint:
      'Includes {{n}} operations from this location the server has not confirmed yet (such as waste recorded without internet). What is marked "provisional" becomes final once the server confirms it.',
    localNOT_READY: "This PC is making its first copy of the server's data. Please wait a moment.",
    localNOT_REGISTERED: 'This PC is not registered to a location yet.',
    localSIGN_IN_REQUIRED: 'Sign in first.',
    localNOT_ALLOWED: 'You may not do this at this location.',
    localSERVER_UNREACHABLE:
      "The server does not answer, so your normal sign-in cannot be confirmed. Sign in without internet to work on this PC's copy.",
    backToOffline: 'Back',
    localWasteTitle: 'Waste recorded on this PC',
    localWasteIntro:
      'The waste and its photos are saved on this PC first, in one step, then sent once when there is internet. Available quantities are not shown here.',
    localWasteEmpty: 'No waste recorded on this PC yet.',
    localWasteSaved:
      'The waste and its photos are saved on this PC. Sent once when there is internet.',
    localWasteAlready: 'This waste was already saved on this PC; it was not saved a second time.',
    localPickPhoto: 'Choose a photo on this PC',
    localPhotoHint:
      'At least one photo. The warehouse PC has no camera: choose a photo saved on it (for example one copied from a phone).',
    localLines: 'Items: {{n}}',
    localStateWaiting: 'Waiting to be sent',
    localStateArrived: 'Arrived',
    localStateRefused: 'Refused: {{reason}}',
    localPhotosWaiting: 'Photos not sent yet: {{n}}',
    localPhotosRefused: 'Photos the server refused: {{n}} — tell IT',
    localInvalid: 'The waste could not be saved: check the fields.',
    stockEmpty: 'No stock of this item yet.',
    averageCost: 'Average cost',
    perUnit: '{{price}} per {{unit}}',
    movementsTitle: 'Item movements',
    movementsHint:
      'Every movement in order with the balance after it, who made it and from which device. Values for those who may see costs.',
    movementsEmpty: 'No movement yet.',
    allKinds: 'All movements',
    kind: 'Movement',
    balanceAfter: 'Balance after',
    by: 'By',
    document: 'Document',
    reversed: 'Reversed',
    matchTitle: 'Stock and the books',
    matchHint:
      'Every night at 6:30 and after each update, the system checks that the stock value at each location equals its account in the books, and that every balance equals the sum of its movements.',
    matchOk: 'Matches',
    matchDifferent: 'Differences found',
    matchNever: 'Not checked yet.',
    checkNow: 'Check now',
    lastCheck: 'Last check: {{time}} ({{kind}})',
    runNIGHTLY: 'nightly',
    runDEPLOY: 'after an update',
    runON_DEMAND: 'on request',
    checked: '{{locations}} location(s), {{pools}} stock balance(s)',
    hiddenDifferences: 'and {{n}} difference(s) at locations you do not see.',
    expected: 'Expected',
    actual: 'Found',
    check: 'Check',
    kindOPENING: 'Opening stock',
    kindPURCHASE_RECEIPT: 'Purchase receipt',
    kindSUPPLIER_RETURN: 'Return to supplier',
    kindPRODUCTION_INPUT: 'Production input',
    kindPRODUCTION_OUTPUT: 'Production output',
    kindTRANSFER_OUT: 'Shipped to a branch',
    kindTRANSFER_IN: 'Shipment received',
    kindTRANSIT_FOUND: 'Found after shipping',
    kindBRANCH_RETURN_OUT: 'Returned by the branch',
    kindBRANCH_RETURN_IN: 'Branch return received',
    kindWASTE: 'Waste',
    kindCOUNT_ADJUSTMENT: 'Count adjustment',
    kindCONSUMPTION: 'Consumption',
    kindCOST_ADJUSTMENT: 'Cost adjustment',
    kindBATCH_RECLASS: 'Moved between batches',
    kindREVERSAL: 'Reversal (correction)',
    checkSTOCK_VS_LEDGER: 'Stock value differs from the inventory account',
    checkWRONG_INVENTORY_ACCOUNT: 'An entry on an inventory account that is not this location’s',
    checkTRANSIT_NOT_EMPTY: 'A balance on “Inventory in transit” before any shipment',
    checkREVIEW_NOT_EMPTY: 'A balance on “Stock differences under review”',
    checkBALANCE_QUANTITY: 'The balance quantity differs from its movements',
    checkBALANCE_VALUE: 'The balance value differs from its movements',
    checkBALANCE_MISSING: 'Movements without a balance',
    checkBATCH_QUANTITY: 'A batch quantity differs from its movements',
    checkPARTY_UNNAMED: 'Lines on a party account without their party',
  },
  staff: {
    appName: 'Dob Staff',
    /** A version with the build number EAS gave it (owner, 2026-09-30). */
    versionWithBuild: '{{version}} (build {{build}})',
    /** Notifications on the phone (M8-3). Push text is never sensitive (D183). */
    push: {
      title: 'Notifications',
      channelName: 'Dob alerts',
      shortOn: 'On',
      shortOff: 'Off',
      checking: 'Checking notifications…',
      on: 'Notifications are on for this phone.',
      notAsked:
        'You have not allowed notifications on this phone yet. Tap "Allow notifications", then "Allow".',
      blocked:
        'Notifications are not allowed for this app. Tap "Allow notifications", then "Allow".',
      blockedSettings:
        'Notifications are blocked for this app in the phone settings. Open Settings → Notifications and turn them on for Dob Staff.',
      notSetUp:
        'This version of the app cannot receive notifications yet (Firebase set-up not finished). A coming version will.',
      offline:
        'Turning on notifications needs the internet. The app finishes by itself when it is back.',
      failed:
        'Notifications could not be turned on ({{code}}). Try again; if it keeps happening, send a screenshot of this screen to IT.',
      allow: 'Allow notifications',
      openSettings: 'Open phone settings',
      retry: 'Try again',
      test: 'Send me a test notification',
      testSent:
        'Sent. It reaches the phones you are signed in on within seconds (with the app open, it shows at the top).',
      testNoPhone:
        'No phone is registered for notifications on your account yet. Wait a moment and try again.',
      testStub:
        'The server does not really send notifications yet (test mode), so nothing was sent.',
      privacy:
        'A notification never carries amounts, salaries or anything sensitive; the details are only inside the app, in "My alerts".',
    },
    selfCheck: {
      title: 'App check',
      subtitle: 'Test build (M8-0): checks that the app works on this phone.',
      running: 'Checking…',
      allPassed: 'All checks passed ({{passed}})',
      failedNames: 'Failed:',
      someFailed: '{{failed}} checks failed — send a photo of this screen to IT',
      ok: 'Passed',
      failed: 'Failed',
      info: 'For information',
      device: 'This phone',
      runAgain: 'Check again',
      duration: '{{ms}} ms',
    },
    signIn: {
      subtitle: 'The Dob Burger staff app',
      identifier: 'Phone number',
      identifierHint: 'Example: 07701234567 — Arabic or English digits are both fine.',
      needsInternet: 'Signing in needs the internet. Check the connection and try again.',
      appCheck: 'App check',
      version: 'Version {{version}}',
    },
    mfaSetup: {
      step1:
        'Install Google Authenticator or Microsoft Authenticator on this phone (from Google Play or the App Store).',
      step2:
        'Tap “Open the authenticator app” to add your account there, or copy the key and add it in the app by hand.',
      step3: 'Come back here and enter the 6-digit code the app shows.',
      openApp: 'Open the authenticator app',
      noApp:
        'No authenticator app opened. Install Google Authenticator or Microsoft Authenticator and tap the button again, or copy the key and enter it by hand.',
      key: 'Key (to enter by hand)',
      copyKey: 'Copy the key',
      keyCopied: 'Key copied. Paste it into the authenticator app.',
    },
    recovery: {
      copyAll: 'Copy the codes',
      copied:
        'Codes copied. Paste them somewhere safe (such as a password manager), then copy any other word so they do not stay on the clipboard.',
    },
    forgot: {
      needsInternet: 'Resetting a password needs internet. Try again when the phone is connected.',
      notYet:
        'Resetting a password from the phone will work once WhatsApp sending is switched on. Until then, ask your manager or IT to reset your password.',
    },
    sync: {
      checking: 'Checking the connection…',
      online: 'Online',
      offline: 'Offline',
      offlinePending: 'Offline — {{count}} operations not sent yet',
      pending: '{{count}} operations not sent yet',
      storeProblem:
        "This phone's data has not moved into its encrypted database yet. Sending goes on, but no new data arrives until the move works; it is tried again when the app opens next time. Do not delete the app or clear its data — tell IT if this message stays.",
      storeLost:
        "This phone's data could not be opened, so nothing is sent from it now. Sign in again: the phone starts a new database and your data is downloaded again. Management and IT are told about any entries that were not sent and may be lost.",
      storeKeyLost:
        "The key of this phone's database is missing, so what was saved on it cannot be opened. When you sign in, the phone starts a new database, and management and IT are told about any entries that were not sent and may be lost.",
      storeKeyError:
        "The key of this phone's database could not be read just now, so nothing is sent. Close the app and open it again; if this happens three times, the phone starts a new database after you sign in. Do not delete the app or clear its data.",
      signInAgain: 'Sign in again',
      storeUnreadable:
        "This phone's data folder could not be read, so nothing is sent now. Close the app and open it again. Do not delete the app or clear its data — call IT if this message stays.",
    },
    home: {
      hello: 'Hello {{name}}',
      soon: 'Work features (schedule, attendance, requests, payslips) come in the next phases.',
      savedOffline: 'Offline: showing the last data saved on this phone.',
    },
    tabs: {
      home: 'Home',
      myWork: 'My work',
      work: 'Manage',
      more: 'More',
    },
    phase: 'Coming in phase {{phase}}',
    myWork: {
      title: 'My work',
      intro: 'Your schedule, attendance, requests and payslips will be here.',
      schedule: 'Work schedule',
      attendance: 'Attendance',
      requests: 'Requests (leave, advance)',
      payslips: 'Payslips',
    },
    work: {
      title: 'Manage',
      intro: 'The tools of your role. They are built in the next phases of the project.',
      approvals: 'Approvals',
      reports: 'Reports',
      finance: 'Finance',
      warehouse: 'Warehouse',
      hr: 'Human resources',
      quality: 'Quality',
      audit: 'Audit',
      branch: 'Branch',
      deliveries: 'Deliveries',
      waste: 'Waste and damage',
      wasteDetail: 'Record it even without internet',
      receive: 'Receive goods',
      receiveDetail: 'Count what arrived, even without internet',
      handoff: 'Photo for the PC',
      handoffDetail: "An invoice's or damage photo for the PC's screen, through its code",
    },
    waste: {
      title: 'Record waste or damage',
      intro:
        'Saved on this phone and sent once when there is internet. The quantities available are not shown here.',
      location: 'Location',
      chooseLocation: 'Choose the location.',
      noPlace: 'There is no location on this phone where you record waste.',
      item: 'Item',
      search: 'Search an item by name or code',
      noItem: 'No item by that name at this location.',
      batch: 'Batch',
      batchOption: '{{code}} — expires {{expiry}}',
      quantity: 'Quantity ({{unit}})',
      unitG: 'kg',
      unitML: 'L',
      unitPC: 'pcs',
      addItem: 'Add at least one item.',
      addLine: 'Add another item',
      removeLine: 'Remove this item',
      reason: 'Reason',
      reasonEXPIRED: 'Expired',
      reasonDAMAGED: 'Damaged',
      reasonSPOILED: 'Spoiled',
      reasonOTHER: 'Other reason',
      chooseReason: 'Choose the reason.',
      reasonText: 'Write the reason',
      reasonTextRequired: 'Write the reason (at least 3 characters).',
      note: 'Note (optional)',
      photos: 'Photos',
      takePhoto: 'Take a photo',
      photoCount: 'Photos: {{count}}',
      removePhoto: 'Remove the photo',
      noCameraPermission:
        "Cannot take a photo: allow Dob Staff to use the camera in the phone's settings.",
      save: 'Record the waste',
      saved: 'The waste is saved on this phone. It is sent once when there is internet.',
      notValid: 'The waste could not be prepared. Check the fields.',
      sent: 'What you recorded on this phone',
      nothingYet: 'Nothing yet.',
      lines: 'Items: {{count}}',
      stateWaiting: 'Waiting to be sent',
      stateArrived: 'Arrived',
      stateRefused: 'Refused: {{reason}}',
      photosWaiting: 'Photos not sent yet: {{count}}',
      photosRefused: 'Photos refused by the server: {{count}} — tell IT',
      missingTitle: 'The waste is not recorded yet. Missing:',
      lineN: 'Item {{n}}',
      quantityField: 'Quantity',
      photosProblem: 'Photos have not gone for {{minutes}} minutes although the phone is online.',
      photosProblemStep_link: 'Stopped when asking the server for the upload link.',
      photosProblemStep_upload: 'Stopped when uploading the photo to storage.',
      photosProblemStep_confirm: 'Stopped when asking the server to confirm.',
      photosProblemHint:
        'The photos are kept on this phone and will not be deleted; the phone tries every minute. If it stays like this, tell IT this reason:',
    },
    // W5-6 (O68): the camera check, opened by a link only (dobstaff://camera-check).
    cameraCheck: {
      title: 'Camera check',
      intro: 'Tries the camera as the app uses it; sends nothing and keeps no photo.',
      photo: 'Try taking a photo',
      qr: 'Try the code reader',
    },
    // W5-6: «Photo for the PC» — the photo for a page's QR code, never kept on the phone.
    handoff: {
      title: 'Photo for the PC',
      intro: "Point the camera at the code on the PC's screen.",
      noneKept: 'The photos are sent at once and never kept on this phone.',
      allowCamera: 'Allow the camera',
      notOurs: 'This is not a «Take it with the phone» code of the Dob system.',
      purposeINVOICE: "An invoice's photo",
      purposeDAMAGE: 'Photos of damaged goods',
      forPlace: 'For {{place}}',
      notHere: 'This code is for a place where you do not work; it may be refused.',
      validFor: 'The code works for {{minutes}}:{{seconds}}',
      expired: 'The code has expired. Show a new code on the PC.',
      take: 'Take a photo',
      takeMore: 'Take another part',
      sending: 'Sending…',
      arrived: "Photo {{n}} arrived. Look at the PC's screen.",
      offline:
        'No internet — the photo is not kept on the phone. Take it when the connection is back.',
      refused: 'Refused: {{reason}}',
      failed: 'The photo did not arrive. Try again. Reason: {{reason}}',
      another: 'Read another code',
    },
    // W5-5: the blind receipt on the phone (the form's words come from `receiving`).
    receive: {
      title: 'Receive goods',
      intro:
        "Invoices waiting for their goods. Count what really arrived: the invoice's quantities and prices are not shown here. The count is kept on this phone and sent once when there is internet.",
      place: 'Warehouse',
      noPlace: 'There is no warehouse on this phone where you receive goods.',
      taskDetail: 'Invoice {{number}} — {{date}}',
      open: 'Count what arrived',
      unitOption: '{{name}} ({{factor}})',
      day: 'Day',
      month: 'Month',
      year: 'Year',
      dateInvalid: 'This date is not right. Type the day, month and year in numbers.',
      lineMissing: 'Every item on the invoice needs a count (0 if none arrived).',
      notValid: 'The receipt could not be prepared. Check the fields.',
      notSaved:
        'The receipt was not saved on this phone and nothing was sent. Try again; if it happens again, tell IT.',
      noItem: 'No item with this name.',
      damagePhotos: 'Damage photos',
      missingTitle: 'The receipt is not saved yet. Missing:',
      back: 'Back',
      saved: 'The receipt is saved on this phone. It is sent once when there is internet.',
      sent: 'What I received on this phone',
      sentLine: '{{supplier}} — invoice {{number}}',
      sentUnknown: 'Receipt',
    },
    more: {
      title: 'More',
      profile: 'My profile',
      sessions: 'My devices',
      settings: 'Settings',
      password: 'Change password',
      alerts: 'My alerts',
    },
    alerts: {
      title: 'My alerts',
      none: 'No alerts.',
      new: 'New',
    },
    profile: {
      title: 'My profile',
      nameAr: 'Name in Arabic',
      nameEn: 'Name in English',
      phone: 'Phone number',
      roles: 'My roles',
      twoFactorOn: 'Two-step verification is on',
      twoFactorOff: 'Two-step verification is off',
      passwordLong:
        'Your password must be at least 16 characters (a management account that signs in offline).',
    },
    sessions: {
      title: 'My devices',
      intro:
        'The devices signed in to your account. If you see one you do not know, sign it out and change your password.',
      thisPhone: 'This phone',
      signedIn: 'Signed in: {{time}}',
      lastUsed: 'Last used: {{time}}',
      signOut: 'Sign out this device',
      signOutOthers: 'Sign out all other devices',
      signedOut: 'The device was signed out.',
      signedOutOthers: 'The other devices were signed out.',
      kindStaff: 'Dob Staff app {{version}} — {{detail}}',
      kindDesktop: 'Dob Desktop {{version}}',
      kindBrowser: '{{browser}} on {{os}}',
      kindUnknown: 'Unknown device',
    },
    settings: {
      title: 'Settings',
      restartTitle: 'The app needs to restart',
      restartBody:
        'The language changed. The app restarts once so the screen direction changes too.',
      restartNow: 'Restart now',
    },
    password: {
      title: 'Change password',
    },
  },
};
